// ============================================================
// Advertise Me — shared components
// ============================================================
import React, { useState, useEffect, useRef, useMemo, useLayoutEffect } from 'react';

// ---------------- ScrollReveal -----------------
function ScrollReveal({ children, className = '', as: As = 'div', stagger = false, threshold = 0.15, ...rest }) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {if (e.isIntersecting) {setSeen(true);io.disconnect();}}),
      { threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const cls = `${stagger ? 'reveal-stagger' : 'reveal'} ${seen ? 'in' : ''} ${className}`.trim();
  return <As ref={ref} className={cls} {...rest}>{children}</As>;
}

// ---------------- Parallax (cursor) -----------------
function CursorParallax({ children, strength = 12, className = '', style = {} }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf;
    let tx = 0,ty = 0,cx = 0,cy = 0;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - (r.left + r.width / 2)) / r.width;
      const y = (e.clientY - (r.top + r.height / 2)) / r.height;
      tx = x * strength;
      ty = y * strength;
    };
    const tick = () => {
      cx += (tx - cx) * 0.08;
      cy += (ty - cy) * 0.08;
      el.style.transform = `translate3d(${cx}px, ${cy}px, 0)`;
      raf = requestAnimationFrame(tick);
    };
    window.addEventListener('mousemove', onMove);
    raf = requestAnimationFrame(tick);
    return () => {window.removeEventListener('mousemove', onMove);cancelAnimationFrame(raf);};
  }, [strength]);
  return <div ref={ref} className={`parallax-host ${className}`} style={style}>{children}</div>;
}

// ---------------- ScrollScene -----------------
// Fixed-position animated backdrop + persistent scroll-driven elements.
// Writes --sy (scroll-y in px, unitless) and --spct (0..1) to <html> on every frame.
function ScrollScene() {
  const targetY = useRef(0);
  const currentY = useRef(0);
  useEffect(() => {
    let raf;
    const tick = () => {
      currentY.current += (targetY.current - currentY.current) * 0.18;
      const sy = currentY.current;
      const h = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      const pct = Math.max(0, Math.min(1, sy / h));
      const root = document.documentElement;
      root.style.setProperty('--sy', sy.toFixed(1));
      root.style.setProperty('--spct', pct.toFixed(4));
      raf = requestAnimationFrame(tick);
    };
    const onScroll = () => {targetY.current = window.scrollY;};
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    raf = requestAnimationFrame(tick);
    return () => {window.removeEventListener('scroll', onScroll);cancelAnimationFrame(raf);};
  }, []);
  return (
    <div className="scene" aria-hidden="true">
      <div className="scene-pattern" />
      <div className="scene-blob scene-blob--1" />
      <div className="scene-blob scene-blob--2" />
      <div className="scene-blob scene-blob--3" />
      <div className="scene-blob scene-blob--4" />
      <svg className="scene-ring" viewBox="0 0 200 200">
        <circle cx="100" cy="100" r="92" fill="none" stroke="var(--accent)" strokeOpacity="0.5" strokeWidth="0.8" strokeDasharray="2 8" />
        <circle cx="100" cy="100" r="74" fill="none" stroke="var(--ink)" strokeOpacity="0.12" strokeWidth="0.6" />
        <circle cx="100" cy="100" r="3" fill="var(--accent)" />
      </svg>
      <svg className="scene-target" viewBox="0 0 60 60">
        <circle cx="30" cy="30" r="22" fill="none" stroke="var(--ink)" strokeOpacity="0.5" strokeWidth="0.8" />
        <line x1="0" y1="30" x2="22" y2="30" stroke="var(--ink)" strokeOpacity="0.5" strokeWidth="0.8" />
        <line x1="38" y1="30" x2="60" y2="30" stroke="var(--ink)" strokeOpacity="0.5" strokeWidth="0.8" />
        <line x1="30" y1="0" x2="30" y2="22" stroke="var(--ink)" strokeOpacity="0.5" strokeWidth="0.8" />
        <line x1="30" y1="38" x2="30" y2="60" stroke="var(--ink)" strokeOpacity="0.5" strokeWidth="0.8" />
      </svg>
    </div>);

}

// Right-edge HUD — scroll progress, percentage, section label
function ScrollHUD() {
  const [section, setSection] = useState('— intro');
  const [pct, setPct] = useState(0);
  useEffect(() => {
    let raf;
    const update = () => {
      const sy = window.scrollY;
      const h = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      setPct(Math.max(0, Math.min(1, sy / h)));
      const mid = sy + window.innerHeight * 0.4;
      const els = document.querySelectorAll('[data-section]');
      let active = '— intro';
      els.forEach((el) => {
        const t = el.offsetTop;
        const b = t + el.offsetHeight;
        if (mid >= t && mid <= b) active = el.dataset.section;
      });
      setSection(active);
    };
    const onScroll = () => {cancelAnimationFrame(raf);raf = requestAnimationFrame(update);};
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();
    return () => {window.removeEventListener('scroll', onScroll);window.removeEventListener('resize', onScroll);cancelAnimationFrame(raf);};
  }, []);
  const pctStr = String(Math.round(pct * 100)).padStart(3, '0');
  return (
    <div className="scroll-hud" aria-hidden="true">
      <div className="scroll-hud-top">
        <div className="scroll-hud-label mono">SCROLL</div>
        <div className="scroll-hud-pct mono">{pctStr}<span>%</span></div>
      </div>
      <div className="scroll-hud-rail">
        <div className="scroll-hud-fill" style={{ height: `${pct * 100}%` }} />
        <div className="scroll-hud-cursor" style={{ top: `${pct * 100}%` }} />
      </div>
      <div className="scroll-hud-bottom mono">{section}</div>
    </div>);

}

// ---------------- BrandMark -----------------
function BrandMark({ size = 22 }) {
  return (
    <span className="nav-brand-mark" style={{ width: size, height: size }} />);

}

// ---------------- KineticHero -----------------
// Cycles a noun and morphs accent shape; reads `mode` from tweaks
function KineticHero({ mode = 'morph', accent = '#0066ff', eyebrow, subtext, ctaPrimary, metaBar }) {
  const words = ['signage.', 'kiosks.', 'wayfinding.', 'rosters.', 'ePosters.', 'experiences.'];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIdx((i) => (i + 1) % words.length), 2400);
    return () => clearInterval(id);
  }, []);

  const _eyebrow = eyebrow || 'Australian-made digital solutions since 2009';
  const _subtext = subtext || 'Custom digital signage, interactive touch solutions, wayfinding systems and directory boards for medical centres, shopping centres and hospitals. Designed, built and supported in Sydney, Australia.';
  const _ctaPrimary = ctaPrimary || 'Get a quote';
  const _metaBar = metaBar || ['Touch solutions', 'Wayfinding & directories', 'Bed management', 'ePosters'];

  return (
    <section className="hero">
      <div className="container hero-inner">
        <div className="hero-top">
          <span className="eyebrow"><span className="dot"></span>{_eyebrow}</span>
        </div>

        <h1 className="display hero-title">
          <span className="hero-line">Digital </span>
          <span className="hero-line">
            <span className="hero-rotate">
              <span key={idx} className="hero-word">
                {words[idx]}
              </span>
            </span>
          </span>
          <span className="hero-line hero-line--em">
            Built in <span className="hero-em">Australia.</span>
          </span>
        </h1>

        <div className="hero-bottom">
          <p className="lede hero-lede">{_subtext}</p>
          <div className="hero-cta">
            <a className="btn btn--accent btn--lg" data-link href="#/quote">
              {_ctaPrimary}
              <span className="arrow">
                <svg viewBox="0 0 16 16" width="14" height="14"><path d="M4 12 L12 4 M6 4 H12 V10" stroke="currentColor" strokeWidth="1.4" fill="none" strokeLinecap="round" /></svg>
              </span>
            </a>
            <button
              className="btn btn--ghost btn--lg"
              onClick={() => {
                const el = document.getElementById('what-we-deploy');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            >
              Explore products
            </button>
          </div>
        </div>

        {/* Animated kinetic backdrop */}
        <div className={`hero-canvas hero-canvas--${mode}`} aria-hidden="true">
          <CursorParallax strength={18}>
            <HeroBackdrop mode={mode} idx={idx} />
          </CursorParallax>
        </div>
      </div>

      <div className="hero-meta container">
        {_metaBar.map((label, i) => (
          <div key={i}><span className="mono">0{i+1}</span> &nbsp; {label}</div>
        ))}
      </div>
    </section>);

}

function HeroBackdrop({ mode, idx }) {
  // Modes: 'morph' (orbiting shapes), 'grid' (animated dot wave), 'wordmark' (oversize type)
  if (mode === 'grid') return <BackdropDots />;
  if (mode === 'wordmark') return <BackdropWordmark idx={idx} />;
  return <BackdropMorph idx={idx} />;
}

function BackdropMorph({ idx }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let W, H, raf, t = 0, pathProgress = 0, rosterFlash = 0, dirFlash = 0, rosterActive = 0, mouseX = 0.5, mouseY = 0.5;
    let boot = 0;            // 0..1 boot-in progress
    let focus = [0,0,0,0];   // per-screen focus level (eased)
    let focusT = [0,0,0,0];  // per-screen focus target
    let hoverIdx = -1;       // which screen the cursor is over
    // screen rects in normalized canvas space, filled each frame for hit-testing
    let rects = [null,null,null,null];

    const getAccent = () => getComputedStyle(document.documentElement).getPropertyValue('--accent').trim() || '#0066ff';
    const hexToRgb = (hex) => {
      const h = hex.replace('#','');
      return [parseInt(h.slice(0,2),16), parseInt(h.slice(2,4),16), parseInt(h.slice(4,6),16)];
    };

    const resize = () => { W = canvas.offsetWidth; H = canvas.offsetHeight; canvas.width = W; canvas.height = H; };
    resize();

    const NODE_COUNT = 40;
    const MAX_DIST = 150;
    let nodes = [];
    const initNodes = () => {
      nodes = Array.from({ length: NODE_COUNT }, () => ({
        x: Math.random() * W, y: Math.random() * H,
        vx: (Math.random() - 0.5) * 0.28, vy: (Math.random() - 0.5) * 0.28,
        r: 1.2 + Math.random() * 1.4, pulse: Math.random() * Math.PI * 2,
        accent: Math.random() < 0.14
      }));
    };
    initNodes();

    const onMouse = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseX = (e.clientX - rect.left) / rect.width;
      mouseY = (e.clientY - rect.top) / rect.height;
      const cxp = mouseX * W, cyp = mouseY * H;
      let found = -1;
      for (let i = 0; i < 4; i++) {
        const r = rects[i];
        if (r && cxp >= r.x && cxp <= r.x + r.w && cyp >= r.y && cyp <= r.y + r.h) { found = i; break; }
      }
      hoverIdx = found;
    };
    document.addEventListener('mousemove', onMouse);
    const onLeave = () => { hoverIdx = -1; };
    canvas.addEventListener('mouseleave', onLeave);

    const rosterStaff = [
      { name: 'Dr. Sarah Chen',     role: 'GP',          status: 'IN',  room: '1', seed: 0.2 },
      { name: 'Dr. James Nguyen',   role: 'GP',          status: 'OUT', room: '-', seed: 0.7 },
      { name: 'Dr. Priya Sharma',   role: 'Specialist',  status: 'IN',  room: '3', seed: 0.4 },
      { name: 'Nurse Kim Torres',   role: 'Nurse',       status: 'IN',  room: '2', seed: 0.1 },
      { name: 'Dr. Mark Ellis',     role: 'GP',          status: 'OUT', room: '-', seed: 0.9 },
      { name: 'Dr. Anita Kapoor',   role: 'Paediatrics', status: 'IN',  room: '4', seed: 0.35 },
      { name: 'Dr. Tom Walker',     role: 'GP',          status: 'IN',  room: '5', seed: 0.6 },
      { name: 'Nurse Jo Bennett',   role: 'Nurse',       status: 'OUT', room: '-', seed: 0.8 },
      { name: 'Dr. Lena Fischer',   role: 'Dermatology', status: 'IN',  room: '6', seed: 0.15 },
      { name: 'Reception',          role: 'Admin',       status: 'IN',  room: 'R', seed: 0.5 },
    ];

    const dirEntries = [
      { name: 'Advertise Me',   level: 'G',  suite: '01', open: true  },
      { name: 'City Dental',    level: '1',  suite: '12', open: true  },
      { name: 'Medibank',       level: '1',  suite: '14', open: false },
      { name: 'Skin Clinic',    level: '1',  suite: '16', open: true  },
      { name: 'Optus',          level: '2',  suite: '22', open: true  },
      { name: 'CommBank',       level: '2',  suite: '24', open: true  },
      { name: 'Ray White',      level: '2',  suite: '26', open: false },
      { name: 'Specsavers',     level: '3',  suite: '30', open: true  },
      { name: 'Lush',           level: '3',  suite: '31', open: false },
      { name: 'JB Hi-Fi',       level: '3',  suite: '35', open: true  },
      { name: 'Priceline',      level: 'G',  suite: '04', open: true  },
      { name: 'Terry White',    level: 'G',  suite: '06', open: false },
    ];

    const pad = (x, y, w, h, r, fill, stroke, sw) => {
      ctx.beginPath(); ctx.roundRect(x, y, w, h, r);
      if (fill) { ctx.fillStyle = fill; ctx.fill(); }
      if (stroke) { ctx.strokeStyle = stroke; ctx.lineWidth = sw || 1; ctx.stroke(); }
    };
    const corners = (x, y, w, h, len, color, lw) => {
      ctx.strokeStyle = color; ctx.lineWidth = lw || 1.2;
      [[x,y,1,1],[x+w,y,-1,1],[x,y+h,1,-1],[x+w,y+h,-1,-1]].forEach(([cx,cy,sx,sy]) => {
        ctx.beginPath(); ctx.moveTo(cx+sx*len,cy); ctx.lineTo(cx,cy); ctx.lineTo(cx,cy+sy*len); ctx.stroke();
      });
    };
    const lbl = (text, x, y, size, color, align, font) => {
      ctx.font = `500 ${size}px ${font || 'JetBrains Mono, monospace'}`;
      ctx.fillStyle = color; ctx.textAlign = align || 'left';
      ctx.fillText(text, x, y);
    };

    const draw = () => {
      t += 0.013;
      boot += (1 - boot) * 0.025;           // ease boot 0->1
      const bootE = boot < 0.999 ? (1 - Math.pow(1 - boot, 3)) : 1; // easeOutCubic
      // auto-cycle a "spotlight" when the user isn't hovering, so it feels alive
      const autoIdx = Math.floor(t / 3) % 4;
      for (let i = 0; i < 4; i++) {
        focusT[i] = (hoverIdx === -1 ? (i === autoIdx ? 0.5 : 0) : (i === hoverIdx ? 1 : 0));
        focus[i] += (focusT[i] - focus[i]) * 0.08;
      }
      pathProgress = (pathProgress + 0.0035) % 1;
      rosterFlash += 0.016;
      dirFlash += 0.014;
      if (Math.floor(rosterFlash / 2.5) !== Math.floor((rosterFlash - 0.016) / 2.5)) {
        rosterActive = (rosterActive + 1) % rosterStaff.length;
      }

      ctx.clearRect(0, 0, W, H);

      // per-screen parallax offsets from mouse position
      const mx = (mouseX - 0.5) * 2;
      const my = (mouseY - 0.5) * 2;
      const px = [
        { x: mx * -8,  y: my * -6  },
        { x: mx * 12,  y: my * -10 },
        { x: mx * -6,  y: my * 8   },
        { x: mx * 10,  y: my * 6   },
      ];
      const [ar, ag, ab] = hexToRgb(getAccent());
      // bob offset per screen for gentle floating
      const bob = (i) => Math.sin(t * 0.8 + i * 1.7) * 3;
      // begin a screen: lift/scale by focus, record rect for hit-testing, fade in on boot
      const beginScreen = (i, x, y, w, h, pxo) => {
        rects[i] = { x: x + pxo.x, y: y + pxo.y + bob(i), w, h };
        const f = focus[i];
        ctx.save();
        ctx.translate(pxo.x, pxo.y + bob(i));
        // focus lift + subtle scale around the screen centre
        const cx = x + w/2, cy = y + h/2;
        const sc = 1 + f * 0.04;
        ctx.translate(cx, cy); ctx.scale(sc, sc); ctx.translate(-cx, -cy);
        ctx.globalAlpha = (0.5 + 0.5 * bootE) * (0.55 + 0.45 * (hoverIdx === -1 ? 1 : (0.4 + 0.6 * f)));
      };
      // glow ring drawn behind a focused screen
      const focusGlow = (i, x, y, w, h) => {
        const f = focus[i];
        if (f < 0.02) return;
        ctx.save();
        ctx.shadowColor = ac(0.5 * f);
        ctx.shadowBlur = 30 * f;
        pad(x, y, w, h, 6, null, ac(0.5 * f), 1.5);
        ctx.restore();
      };
      const ac = (a) => `rgba(${ar},${ag},${ab},${a})`;
      const ink = (a) => `rgba(10,10,10,${a})`;

      // ---- particles ----
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x, dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx*dx + dy*dy);
          if (dist < MAX_DIST) {
            ctx.beginPath(); ctx.moveTo(nodes[i].x, nodes[i].y); ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = ac((1 - dist/MAX_DIST) * 0.08); ctx.lineWidth = 0.6; ctx.stroke();
          }
        }
      }
      nodes.forEach(n => {
        n.pulse += 0.025;
        const glow = 0.5 + Math.sin(n.pulse) * 0.5;
        if (n.accent) {
          const g = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, n.r * 7);
          g.addColorStop(0, ac(0.18)); g.addColorStop(1, ac(0));
          ctx.beginPath(); ctx.arc(n.x, n.y, n.r * 7, 0, Math.PI*2); ctx.fillStyle = g; ctx.fill();
        }
        ctx.beginPath(); ctx.arc(n.x, n.y, n.r + (n.accent ? glow : 0), 0, Math.PI*2);
        ctx.fillStyle = n.accent ? ac(0.7 + glow * 0.3) : ink(0.14); ctx.fill();
        n.x += n.vx; n.y += n.vy;
        if (n.x < 0 || n.x > W) n.vx *= -1;
        if (n.y < 0 || n.y > H) n.vy *= -1;
      });

      // ---- connective data-lines between the four screens (behind everything) ----
      const hubs = [
        { x: W*0.02 + 42, y: H*0.03 + H*0.24 },   // kiosk centre-ish
        { x: W*0.24 + W*0.11, y: H*0.03 + H*0.24 },// wayfinding
        { x: W*0.52 + W*0.11, y: H*0.03 + H*0.24 },// directory
        { x: W*0.5 + W*0.23, y: H*0.56 + (W*0.46*9/16)/2 }, // roster
      ];
      const linkPairs = [[0,1],[1,2],[2,3],[0,3]];
      linkPairs.forEach(([a2,b2], li) => {
        const A = hubs[a2], B = hubs[b2];
        const lit = Math.max(focus[a2], focus[b2]);
        ctx.strokeStyle = ac(0.05 + lit * 0.22);
        ctx.lineWidth = 0.5 + lit * 0.5;
        ctx.setLineDash([2, 5]);
        ctx.beginPath(); ctx.moveTo(A.x, A.y); ctx.lineTo(B.x, B.y); ctx.stroke();
        ctx.setLineDash([]);
        // travelling packet
        const pk = ((t * 0.35 + li * 0.27) % 1);
        const packetAlpha = 0.25 + lit * 0.75;
        const pxp = A.x + (B.x - A.x) * pk, pyp = A.y + (B.y - A.y) * pk;
        ctx.beginPath(); ctx.arc(pxp, pyp, 1.4 + lit * 1.2, 0, Math.PI*2);
        ctx.fillStyle = ac(packetAlpha * bootE); ctx.fill();
      });

      // ============================================================
      // SCREEN 1 — TOUCH KIOSK (floor-standing portrait, far left)
      // ============================================================
      const kx = W * 0.02, ky = H * 0.03;
      const kw = 85, kh = H * 0.48;
      const kbw = kw + 30, kbh = 40, kbx = kx - 15, kby = ky + kh;

      beginScreen(0, kx, ky, kw, kh, px[0]);
      focusGlow(0, kx, ky, kw, kh);
      const kg = ctx.createRadialGradient(kx+kw/2, ky+kh/2, 0, kx+kw/2, ky+kh/2, kh*0.55);
      kg.addColorStop(0, ac(0.07)); kg.addColorStop(1, ac(0));
      ctx.beginPath(); ctx.ellipse(kx+kw/2, ky+kh/2, kh*0.38, kh*0.52, 0, 0, Math.PI*2);
      ctx.fillStyle = kg; ctx.fill();

      // pole
      ctx.fillStyle = ink(0.07); ctx.fillRect(kx + kw/2 - 4, kby, 8, kbh * 0.6);
      // plinth
      pad(kbx, kby + kbh*0.6, kbw, kbh*0.4, 3, ink(0.07), ac(0.18), 0.8);
      // body
      pad(kx, ky, kw, kh, 6, ink(0.05), ac(0.28), 1);
      corners(kx, ky, kw, kh, 10, ac(0.5), 1.2);

      // screen
      const sx = kx+4, sy = ky+4, sw2 = kw-8, sh = kh-8;
      pad(sx, sy, sw2, sh, 4, ink(0.03), ac(0.1), 0.5);

      // scan line
      const scanY = sy + ((t * 20) % sh);
      const sg = ctx.createLinearGradient(sx, scanY-5, sx, scanY+5);
      sg.addColorStop(0, ac(0)); sg.addColorStop(0.5, ac(0.14)); sg.addColorStop(1, ac(0));
      ctx.fillStyle = sg; ctx.fillRect(sx+1, scanY-5, sw2-2, 10);

      // header
      pad(sx+2, sy+2, sw2-4, 16, 2, ac(0.35), null, 0);
      lbl('TOUCH KIOSK', sx+sw2/2, sy+12, 7, '#fff', 'center');

      // map dots
      for (let gy = 0; gy < 6; gy++) {
        for (let gx2 = 0; gx2 < 4; gx2++) {
          const dotX = sx + 6 + gx2 * ((sw2-12)/4) + (sw2-12)/8;
          const dotY = sy + 22 + gy * ((sh*0.38)/6) + (sh*0.38)/12;
          const isA = (gx2 === 1 && gy === 2) || (gx2 === 2 && gy === 4);
          ctx.beginPath(); ctx.arc(dotX, dotY, isA ? 2.2 : 1.1, 0, Math.PI*2);
          ctx.fillStyle = isA ? ac(0.9) : ink(0.18); ctx.fill();
        }
      }

      // YOU ARE HERE pulse
      const mX = sx + 6 + 1*(sw2-12)/4 + (sw2-12)/8;
      const mY = sy + 22 + 2*(sh*0.38)/6 + (sh*0.38)/12;
      ctx.beginPath(); ctx.arc(mX, mY, 3.5, 0, Math.PI*2); ctx.fillStyle = ac(1); ctx.fill();
      ctx.beginPath(); ctx.arc(mX, mY, 5.5 + Math.sin(t*3)*1.5, 0, Math.PI*2);
      ctx.strokeStyle = ac(0.3); ctx.lineWidth = 0.8; ctx.stroke();

      // menu buttons
      const menuItems = ['DIRECTORY','SERVICES','MAP','INFO'];
      menuItems.forEach((item, mi) => {
        const my = sy + sh - 4 - (menuItems.length - mi) * 15;
        const isA = mi === Math.floor(t/2) % menuItems.length;
        pad(sx+3, my, sw2-6, 11, 2, isA ? ac(0.2) : ink(0.04), isA ? ac(0.45) : ac(0.1), 0.5);
        lbl(item, sx+sw2/2, my+8, 5, isA ? ac(0.95) : ink(0.28), 'center');
      });

      ctx.restore();

      // ============================================================
      // SCREEN 2 — WAYFINDING MAP (portrait, next to kiosk)
      // ============================================================
      const wx = W * 0.24, wy = H * 0.03, ww = W * 0.22, wh = H * 0.48;

      beginScreen(1, wx, wy, ww, wh, px[1]);
      focusGlow(1, wx, wy, ww, wh);
      const wg = ctx.createRadialGradient(wx+ww/2, wy+wh/2, 0, wx+ww/2, wy+wh/2, ww);
      wg.addColorStop(0, ac(0.06)); wg.addColorStop(1, ac(0));
      ctx.beginPath(); ctx.ellipse(wx+ww/2, wy+wh/2, ww*0.8, wh*0.55, 0, 0, Math.PI*2);
      ctx.fillStyle = wg; ctx.fill();

      pad(wx, wy, ww, wh, 5, ink(0.04), ac(0.25), 1);
      corners(wx, wy, ww, wh, 9, ac(0.5), 1.1);
      pad(wx+3, wy+3, ww-6, 18, 3, ac(0.35), null, 0);
      lbl('WAYFINDING', wx+ww/2, wy+13, 7, '#fff', 'center');

      // grid
      const mgx = wx+7, mgy = wy+22, mgw = ww-14, mgh = wh-32;
      ctx.strokeStyle = ac(0.07); ctx.lineWidth = 0.4;
      for (let gl = 0; gl < 5; gl++) {
        ctx.beginPath(); ctx.moveTo(mgx, mgy+(gl/4)*mgh); ctx.lineTo(mgx+mgw, mgy+(gl/4)*mgh); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(mgx+(gl/4)*mgw, mgy); ctx.lineTo(mgx+(gl/4)*mgw, mgy+mgh); ctx.stroke();
      }

      // rooms
      const rooms = [
        { x:0.04, y:0.04, w:0.28, h:0.2,  label:'FOOD' },
        { x:0.37, y:0.04, w:0.24, h:0.2,  label:'RETAIL' },
        { x:0.66, y:0.04, w:0.3,  h:0.34, label:'CINEMA' },
        { x:0.04, y:0.3,  w:0.22, h:0.26, label:'GYM' },
        { x:0.31, y:0.3,  w:0.3,  h:0.26, label:'FASHION' },
        { x:0.04, y:0.62, w:0.56, h:0.24, label:'ENTRY' },
        { x:0.65, y:0.44, w:0.31, h:0.42, label:'PARK' },
      ];
      rooms.forEach(rm => {
        const rx=mgx+rm.x*mgw, ry=mgy+rm.y*mgh, rw=rm.w*mgw, rh=rm.h*mgh;
        pad(rx, ry, rw, rh, 2, ink(0.04), ac(0.14), 0.4);
        lbl(rm.label, rx+rw/2, ry+rh/2+3, 4, ink(0.22), 'center');
      });

      // path
      const pp = [
        [mgx+mgw*0.12, mgy+mgh*0.86],
        [mgx+mgw*0.12, mgy+mgh*0.58],
        [mgx+mgw*0.12, mgy+mgh*0.42],
        [mgx+mgw*0.45, mgy+mgh*0.42],
        [mgx+mgw*0.45, mgy+mgh*0.16],
        [mgx+mgw*0.75, mgy+mgh*0.16],
      ];
      ctx.setLineDash([3,4]); ctx.strokeStyle = ac(0.45); ctx.lineWidth = 1.1;
      ctx.beginPath(); ctx.moveTo(pp[0][0], pp[0][1]);
      for (let pi=1; pi<pp.length; pi++) ctx.lineTo(pp[pi][0], pp[pi][1]);
      ctx.stroke(); ctx.setLineDash([]);

      // arrows
      [[pp[1],pp[2],'up'],[pp[2],pp[3],'right'],[pp[3],pp[4],'up'],[pp[4],pp[5],'right']].forEach(([f2,t2,dir]) => {
        const mx2=(f2[0]+t2[0])/2, my2=(f2[1]+t2[1])/2, as=3.5;
        ctx.fillStyle = ac(0.6); ctx.beginPath();
        if (dir==='up') { ctx.moveTo(mx2,my2-as); ctx.lineTo(mx2+as,my2+as); ctx.lineTo(mx2-as,my2+as); }
        else { ctx.moveTo(mx2+as,my2); ctx.lineTo(mx2-as,my2-as); ctx.lineTo(mx2-as,my2+as); }
        ctx.closePath(); ctx.fill();
      });

      // travelling dot
      const totalSegs = pp.length-1;
      const segP = pathProgress * totalSegs;
      const segI = Math.min(Math.floor(segP), totalSegs-1);
      const segT2 = segP - segI;
      const tdx = pp[segI][0] + (pp[segI+1][0]-pp[segI][0])*segT2;
      const tdy = pp[segI][1] + (pp[segI+1][1]-pp[segI][1])*segT2;
      ctx.beginPath(); ctx.arc(tdx, tdy, 5+Math.sin(t*4)*1.5, 0, Math.PI*2);
      ctx.strokeStyle = ac(0.28); ctx.lineWidth = 0.7; ctx.stroke();
      ctx.beginPath(); ctx.arc(tdx, tdy, 3, 0, Math.PI*2); ctx.fillStyle = ac(1); ctx.fill();
      ctx.beginPath(); ctx.arc(tdx, tdy, 1.3, 0, Math.PI*2); ctx.fillStyle = '#fff'; ctx.fill();

      ctx.beginPath(); ctx.arc(pp[0][0], pp[0][1], 3.5, 0, Math.PI*2); ctx.fillStyle = ink(0.55); ctx.fill();
      lbl('YOU', pp[0][0]+5, pp[0][1]+4, 4, ink(0.35), 'left');
      ctx.beginPath(); ctx.arc(pp[5][0], pp[5][1], 3.5, 0, Math.PI*2); ctx.fillStyle = ac(1); ctx.fill();
      lbl('CINEMA', pp[5][0]+5, pp[5][1]+4, 4, ac(0.75), 'left');

      ctx.restore();

      // ============================================================
      // SCREEN 3 — DIRECTORY BOARD (portrait, right of wayfinding)
      // ============================================================
      const dx = W * 0.52, dy = H * 0.03, dw = W * 0.22, dh = H * 0.48;

      beginScreen(2, dx, dy, dw, dh, px[2]);
      focusGlow(2, dx, dy, dw, dh);
      const dg = ctx.createRadialGradient(dx+dw/2, dy+dh/2, 0, dx+dw/2, dy+dh/2, dw);
      dg.addColorStop(0, ac(0.06)); dg.addColorStop(1, ac(0));
      ctx.beginPath(); ctx.ellipse(dx+dw/2, dy+dh/2, dw*0.8, dh*0.55, 0, 0, Math.PI*2);
      ctx.fillStyle = dg; ctx.fill();

      pad(dx, dy, dw, dh, 5, ink(0.04), ac(0.25), 1);
      corners(dx, dy, dw, dh, 9, ac(0.5), 1.1);
      pad(dx+3, dy+3, dw-6, 18, 3, ac(0.35), null, 0);
      lbl('DIRECTORY', dx+dw/2, dy+13, 7, '#fff', 'center');

      ctx.strokeStyle = ac(0.12); ctx.lineWidth = 0.4;
      ctx.beginPath(); ctx.moveTo(dx+6, dy+20); ctx.lineTo(dx+dw-6, dy+20); ctx.stroke();

      // column mini-headers
      lbl('NAME', dx+7, dy+28, 3.5, ac(0.45), 'left');
      lbl('LVL', dx+dw*0.6, dy+28, 3.5, ac(0.45), 'left');
      lbl('●', dx+dw-10, dy+28, 4, ac(0.45), 'left');

      const dRowH = (dh - 36) / dirEntries.length;
      const dirBucket = Math.floor(t * 0.5); const dirHL = Math.abs(Math.floor(Math.sin(dirBucket * 127.1) * 9999)) % dirEntries.length;
      dirEntries.forEach((en, ei) => {
        const ey = dy + 32 + ei * dRowH;
        if (ei === dirHL) pad(dx+4, ey, dw-8, dRowH-1, 2, ac(0.12), ac(0.22), 0.5);
        else if (ei%2===0) pad(dx+4, ey, dw-8, dRowH-1, 2, ink(0.025), null, 0);

        ctx.strokeStyle = ac(0.07); ctx.lineWidth = 0.3;
        ctx.beginPath(); ctx.moveTo(dx+6, ey+dRowH-1); ctx.lineTo(dx+dw-6, ey+dRowH-1); ctx.stroke();

        lbl(en.name, dx+7, ey+dRowH/2+1.5, 3.5, ei === dirHL ? ac(0.9) : ink(0.6), 'left', 'Inter, sans-serif');
        lbl(en.level, dx+dw*0.6, ey+dRowH/2+1.5, 3.5, ei === dirHL ? ac(0.8) : ink(0.35), 'left');
        ctx.beginPath(); ctx.arc(dx+dw-8, ey+dRowH/2, 2.5, 0, Math.PI*2);
        ctx.fillStyle = en.open ? ac(0.85) : ink(0.18); ctx.fill();
      });

      const ts = new Date().toLocaleTimeString('en-AU', { hour:'2-digit', minute:'2-digit' });
      lbl(ts, dx+dw/2, dy+dh-5, 4.5, ac(0.35), 'center');

      ctx.restore();

      // ============================================================
      // SCREEN 4 — MEDICAL STAFF ROSTER (landscape, bottom)
      // ============================================================
      const rw2 = W * 0.46, rh2 = rw2 * (9/16), rx2 = W * 0.5, ry2 = H * 0.56;

      beginScreen(3, rx2, ry2, rw2, rh2, px[3]);
      focusGlow(3, rx2, ry2, rw2, rh2);
      const rg = ctx.createRadialGradient(rx2+rw2/2, ry2+rh2/2, 0, rx2+rw2/2, ry2+rh2/2, rw2*0.6);
      rg.addColorStop(0, ac(0.07)); rg.addColorStop(1, ac(0));
      ctx.beginPath(); ctx.ellipse(rx2+rw2/2, ry2+rh2/2, rw2*0.6, rh2*0.55, 0, 0, Math.PI*2);
      ctx.fillStyle = rg; ctx.fill();

      pad(rx2, ry2, rw2, rh2, 5, ink(0.04), ac(0.25), 1);
      corners(rx2, ry2, rw2, rh2, 9, ac(0.5), 1.1);

      // header
      pad(rx2+3, ry2+3, rw2-6, 18, 3, ac(0.35), null, 0);
      lbl('MEDICAL CENTRE', rx2+rw2*0.38, ry2+14, 7, '#fff', 'center');
      lbl('STAFF ROSTER', rx2+rw2*0.72, ry2+14, 7, 'rgba(255,255,255,0.7)', 'left');

      // live badge
      ctx.beginPath(); ctx.arc(rx2+rw2-12, ry2+11, 3, 0, Math.PI*2);
      ctx.fillStyle = ac(0.5+Math.sin(t*4)*0.5); ctx.fill();

      ctx.strokeStyle = ac(0.12); ctx.lineWidth = 0.4;
      ctx.beginPath(); ctx.moveTo(rx2+8, ry2+16); ctx.lineTo(rx2+rw2-8, ry2+16); ctx.stroke();

      // column headers
      const c1=rx2+10, c2=rx2+rw2*0.38, c3=rx2+rw2*0.56, c4=rx2+rw2*0.72, c5=rx2+rw2*0.88;
      lbl('PRACTITIONER', c1, ry2+24, 4, ac(0.45), 'left');
      lbl('ROLE', c2, ry2+24, 4, ac(0.45), 'left');
      lbl('STATUS', c3, ry2+24, 4, ac(0.45), 'left');
      lbl('ROOM', c4, ry2+24, 4, ac(0.45), 'left');
      lbl('SINCE', c5, ry2+24, 4, ac(0.45), 'left');

      const times = ['08:30','09:15','07:45','08:00','10:30','08:15','09:00','07:30','08:45','08:00'];
      const rowTop = ry2 + 26;
      const rowH2 = (rh2 - 32) / rosterStaff.length;
      rosterStaff.forEach((st, si) => {
        const sy2 = rowTop + si * rowH2;
        const isIn = st.status === 'IN';
        const midY = sy2 + rowH2/2;
        const textY = midY + 2;

        // alternating row tint only, no highlight animation
        if (si%2===0) pad(rx2+4, sy2, rw2-8, rowH2, 1, ink(0.02), null, 0);

        ctx.strokeStyle = ac(0.05); ctx.lineWidth = 0.3;
        ctx.beginPath(); ctx.moveTo(rx2+8, sy2+rowH2); ctx.lineTo(rx2+rw2-8, sy2+rowH2); ctx.stroke();

        lbl(st.name, c1, textY, 4.5, ink(0.65), 'left', 'Inter, sans-serif');
        lbl(st.role, c2, textY, 4,   ink(0.32), 'left', 'Inter, sans-serif');

        // Status pill — toggles IN/OUT periodically per doctor using seed offset
        const cycle = Math.floor(t * 0.18 + st.seed * 10) % 2;
        const currentlyIn = isIn ? (cycle === 0) : (cycle === 1);
        const pillBg  = currentlyIn ? ac(0.16) : `rgba(255,160,60,0.18)`;
        const pillBdr = currentlyIn ? ac(0.38) : `rgba(255,160,60,0.55)`;
        const pillTxt = currentlyIn ? ac(0.9)  : `rgba(200,100,20,0.95)`;
        const pillLbl = currentlyIn ? 'IN' : 'OUT';
        const pillH = Math.min(rowH2-3, 9);
        pad(c3, midY - pillH/2, 20, pillH, 2, pillBg, pillBdr, 0.6);
        lbl(pillLbl, c3+10, midY+2, 4, pillTxt, 'center');

        lbl(st.room,   c4, textY, 4.5, ink(0.35), 'left');
        lbl(times[si], c5, textY, 4,   ink(0.25), 'left');
      });

      // footer
      const ts2 = new Date().toLocaleTimeString('en-AU', { hour:'2-digit', minute:'2-digit' });
      lbl(`SYD · ${ts2}  ●  LIVE`, rx2+rw2/2, ry2+rh2-5, 4.5, ac(0.35), 'center');
      ctx.restore();

      raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);
    window.addEventListener('resize', () => { resize(); initNodes(); });
    return () => { cancelAnimationFrame(raf); document.removeEventListener("mousemove", onMouse); };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{ width: '100%', height: '100%', display: 'block', opacity: 0.92 }}
    />
  );
}

function BackdropDots() {
  // grid of dots with a wave traveling across based on time
  const rows = 12,cols = 28;
  const ref = useRef(null);
  useEffect(() => {
    let raf,t = 0;
    const tick = () => {
      t += 0.02;
      const el = ref.current;
      if (el) {
        const dots = el.querySelectorAll('circle');
        dots.forEach((d) => {
          const x = +d.dataset.x,y = +d.dataset.y;
          const v = Math.sin(x * 0.35 + t * 1.5) * 0.5 + Math.cos(y * 0.4 + t) * 0.5;
          const s = 0.6 + (v + 1) * 0.6;
          const o = 0.18 + (v + 1) * 0.22;
          d.setAttribute('r', String(1.4 * s));
          d.setAttribute('opacity', String(o));
        });
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  const dots = [];
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const accent = (x + y) % 17 === 0;
      dots.push(
        <circle key={`${x}-${y}`} cx={40 + x * 42} cy={40 + y * 38} r="1.4"
        data-x={x} data-y={y}
        fill={accent ? 'var(--accent)' : 'var(--ink)'} opacity={accent ? 1 : 0.25} />
      );
    }
  }
  return (
    <svg ref={ref} className="morph" viewBox="0 0 1200 520" preserveAspectRatio="xMidYMid slice">
      {dots}
    </svg>);

}

function BackdropWordmark({ idx }) {
  const words = ['SIGNAGE', 'KIOSKS', 'WAYFIND', 'ROSTERS', 'EPOSTERS', 'BEDS'];
  return (
    <div className="wordmark-bg" aria-hidden="true">
      <div className="wordmark-row">
        {Array.from({ length: 8 }).map((_, i) =>
        <span key={i} className={`wm ${i % 3 === 0 ? 'wm--accent' : ''}`}>{words[(idx + i) % words.length]}</span>
        )}
      </div>
    </div>);

}

// ---------------- SignageWall (mixed landscape + portrait) -----------------
function SignageWall() {
  // Mix: row 1 + row 3 = landscape signage, row 2 = portrait interactive
  const tiles = [
  // Row 1 — landscape signage
  { c: <TileRoster />, name: 'ROSTER · clinic-08', o: 'landscape' },
  { c: <TileBed />, name: 'BED · ward-3', o: 'landscape' },
  { c: <TileDeparture />, name: 'DEPART · gate-04', o: 'landscape' },
  { c: <TilePromo />, name: 'PROMO · retail', o: 'landscape' },
  // Row 2 — portrait, interactive (kiosks, wayfinding, ePosters)
  { c: <TileKiosk />, name: 'KIOSK · foyer', o: 'portrait' },
  { c: <TileWayfind />, name: 'WAYFIND · L2', o: 'portrait' },
  { c: <TileEPoster />, name: 'EPOSTER · 142', o: 'portrait' },
  { c: <TileQueue />, name: 'QUEUE · front-desk', o: 'portrait' },
  // Row 3 — landscape signage
  { c: <TileWeather />, name: 'WEATHER/NEWS · feed', o: 'landscape' },
  { c: <TileMenu />, name: 'CARPARK · reservation', o: 'landscape' },
  { c: <TileStaff />, name: 'STAFF · today', o: 'landscape' },
  { c: <TileAlert />, name: 'MONITOR · live', o: 'landscape' }];

  return (
    <div className="signage-wall">
      <div className="signage-wall-bg" />
      <div className="signage-wall-grid">
        {tiles.map((t, i) =>
        <div key={i} className={`signage-cell signage-cell--${t.o}`} style={{ animationDelay: `${i * 0.08}s` }}>
            <div className={`signage-screen signage-screen--${t.o}`}>{t.c}</div>
            <div className="signage-label mono">
              <span className="mono signage-label-orient">{t.o === 'portrait' ? '▯' : '▭'}</span>
              {t.name}
            </div>
          </div>
        )}
      </div>
    </div>);

}

// ---- Individual tile mocks ----
function TileShell({ children, tone = 'light', title, dot = true }) {
  return (
    <div className={`tile tile--${tone}`}>
      <div className="tile-top">
        <span className="tile-title">{title}</span>
        {dot && <span className="tile-dot" />}
      </div>
      <div className="tile-body">{children}</div>
    </div>);

}
function TileRoster() {
  const names = [
    { n: 'Dr. Singh', s: 'In', c: 1 },
    { n: 'Dr. Lee', s: 'In', c: 1 },
    { n: 'Dr. Patel', s: 'Out', c: 0 },
    { n: 'Dr. Owens', s: 'In', c: 1 },
    { n: 'Dr. Tran', s: 'In', c: 1 },
    { n: 'Dr. Russo', s: 'Out', c: 0 },
    { n: 'Dr. Chen', s: 'In', c: 1 },
  ];
  return (
    <TileShell title="ROSTER · clinic-08">
      <div className="t-list">
        {names.map((d) =>
          <div key={d.n} className="t-row">
            <span>{d.n}</span>
            <span className={"t-status " + (d.c ? "on" : "off")}>{d.s}</span>
          </div>
        )}
      </div>
    </TileShell>);
}
function TileBed() {
  const beds = [1, 1, 2, 3, 1, 1, 0, 2, 1, 3, 1, 1, 0, 1, 2, 1];
  return (
    <TileShell title="Ward 3 · Beds">
      <div className="t-bed-grid">
        {beds.map((s, i) =>
        <span key={i} className={`t-bed t-bed--${s}`} />
        )}
      </div>
      <div className="t-legend">
        <span><i className="t-bed t-bed--0" /> Free</span>
        <span><i className="t-bed t-bed--1" /> Occ.</span>
        <span><i className="t-bed t-bed--2" /> Clean</span>
      </div>
    </TileShell>);

}
function TileKiosk() {
  return (
    <TileShell title="Welcome">
      <div className="t-kiosk t-kiosk--portrait">
        <div className="t-kiosk-hero">
          <div className="t-kiosk-hello">Hello,</div>
          <div className="t-kiosk-sub">how can we help today?</div>
        </div>
        <div className="t-kiosk-list">
          <span><i>◧</i>Browse the directory</span>
          <span><i>⌕</i>Search a store</span>
          <span><i>◈</i>Find on map</span>
          <span><i>?</i>Help &amp; info</span>
        </div>
        <div className="t-kiosk-foot">tap to start →</div>
      </div>
    </TileShell>);

}
function TileWayfind() {
  return (
    <TileShell title="WAYFIND · L2">
      <style>{'@keyframes wayfindDash{to{stroke-dashoffset:-40}}'}</style>
      <svg viewBox="0 0 100 160" className="t-map t-map--portrait" preserveAspectRatio="xMidYMid meet">
        <rect x="6" y="6" width="88" height="44" fill="none" stroke="currentColor" strokeOpacity=".25" />
        <rect x="6" y="58" width="88" height="44" fill="none" stroke="currentColor" strokeOpacity=".4" />
        <rect x="6" y="110" width="88" height="44" fill="none" stroke="currentColor" strokeOpacity=".25" />
        <text x="10" y="16" fontSize="5" fill="currentColor" opacity=".5">L3</text>
        <text x="10" y="68" fontSize="5" fill="currentColor">L2 · YOU ARE HERE</text>
        <text x="10" y="120" fontSize="5" fill="currentColor" opacity=".5">L1</text>
        <path d="M16 138 V 80 H 60 V 28 H 84" stroke="var(--accent)" strokeWidth="1.6" fill="none" strokeDasharray="3 3" style={{animation:'wayfindDash 4s linear infinite'}} />
        <circle cx="16" cy="138" r="3" fill="var(--accent)" />
        <circle cx="84" cy="28" r="3" fill="var(--ink)" />
        <text x="20" y="142" fontSize="4.5" fill="currentColor">You</text>
        <text x="64" y="24" fontSize="4.5" fill="currentColor">Coles</text>
      </svg>
    </TileShell>);
}
function TileEPoster() {
  return (
    <TileShell title="EP-142 · CRISPR" tone="dark">
      <div className="t-poster t-poster--portrait">
        <div className="t-poster-title">CRISPR-Cas9 in retinal models</div>
        <div className="t-poster-authors">Hu · Patel · Ngo — Macquarie</div>
        <div className="t-poster-bars t-poster-bars--portrait">
          <span style={{ height: '40%' }} /><span style={{ height: '70%' }} /><span style={{ height: '55%' }} />
          <span style={{ height: '90%' }} /><span style={{ height: '60%' }} /><span style={{ height: '78%' }} />
        </div>
        <div className="t-poster-lines">
          <span /><span style={{ width: '85%' }} /><span style={{ width: '70%' }} /><span style={{ width: '90%' }} />
        </div>
        <div className="t-tap">tap to read →</div>
      </div>
    </TileShell>);

}
function TileQueue() {
  const [n, setN] = useState(4);
  useEffect(() => {
    const id = setInterval(() => setN((v) => Math.max(0, v + (Math.random() > 0.5 ? -1 : 1))), 1500);
    return () => clearInterval(id);
  }, []);
  return (
    <TileShell title="Now Serving">
      <div className="t-queue t-queue--portrait">
        <div className="t-queue-label mono">NOW SERVING</div>
        <div className="t-bignum t-bignum--xl">{String(108 + n).padStart(3, '0')}</div>
        <div className="t-sub">est. wait <b>~{6 + n} min</b></div>
        <div className="t-queue-list">
          <div className="t-queue-row"><span className="mono">A-{110 + n}</span><span>desk 2</span></div>
          <div className="t-queue-row"><span className="mono">A-{111 + n}</span><span>desk 4</span></div>
          <div className="t-queue-row"><span className="mono">A-{112 + n}</span><span>desk 1</span></div>
        </div>
      </div>
    </TileShell>);

}
function TileDeparture() {
  const [flips, setFlips] = useState({});
  const rows = [
    { dest: 'Newcastle', time: '10:15', plat: '3', status: 'On Time' },
    { dest: 'Melbourne', time: '10:32', plat: '7', status: 'On Time' },
    { dest: 'Brisbane', time: '10:45', plat: '2', status: 'On Time' },
    { dest: 'Canberra', time: '11:00', plat: '5', status: 'On Time' },
    { dest: 'Gold Coast', time: '11:18', plat: '1', status: 'On Time' },
  ];
  const flipTargets = [
    { idx: 0, newStatus: 'Delayed', delay: 2500 },
    { idx: 2, newStatus: 'Boarding', delay: 5000 },
    { idx: 4, newStatus: 'Cancelled', delay: 7500 },
  ];
  useEffect(() => {
    const timers = flipTargets.map(ft => {
      return setTimeout(() => {
        setFlips(prev => ({ ...prev, [ft.idx]: { text: ft.newStatus, flipping: true } }));
        setTimeout(() => setFlips(prev => ({ ...prev, [ft.idx]: { ...prev[ft.idx], flipping: false } })), 600);
      }, ft.delay);
    });
    const reset = setTimeout(() => setFlips({}), 12000);
    const loop = setTimeout(() => setFlips({}), 14000);
    return () => { timers.forEach(clearTimeout); clearTimeout(reset); clearTimeout(loop); };
  }, [flips.reset]);
  useEffect(() => {
    const interval = setInterval(() => setFlips(prev => ({ ...prev, reset: Date.now() })), 14500);
    return () => clearInterval(interval);
  }, []);
  return (
    <TileShell title="DEPART · syd-central" tone="dark">
      <style>{'@keyframes splitFlap{0%{transform:perspective(60px) rotateX(0deg)}50%{transform:perspective(60px) rotateX(-90deg)}51%{transform:perspective(60px) rotateX(90deg)}100%{transform:perspective(60px) rotateX(0deg)}}'}</style>
      <div style={{display:'flex',flexDirection:'column',gap:0,fontSize:'6.5px',width:'100%'}}>
        <div style={{display:'grid',gridTemplateColumns:'2fr 1fr 0.6fr 1.4fr',padding:'2px 6px',gap:4,borderBottom:'1px solid rgba(255,255,255,0.1)'}}>
          <span style={{fontSize:'5px',color:'rgba(255,255,255,0.4)',fontWeight:600,letterSpacing:'0.08em'}}>DEST</span>
          <span style={{fontSize:'5px',color:'rgba(255,255,255,0.4)',fontWeight:600,letterSpacing:'0.08em'}}>TIME</span>
          <span style={{fontSize:'5px',color:'rgba(255,255,255,0.4)',fontWeight:600,letterSpacing:'0.08em'}}>PLT</span>
          <span style={{fontSize:'5px',color:'rgba(255,255,255,0.4)',fontWeight:600,letterSpacing:'0.08em'}}>STATUS</span>
        </div>
        {rows.map((r, i) => {
          const flip = flips[i];
          const status = flip ? flip.text : r.status;
          const isFlipping = flip && flip.flipping;
          const statusColor = status === 'On Time' ? '#4ade80' : '#fbbf24';
          return (
            <div key={i} style={{display:'grid',gridTemplateColumns:'2fr 1fr 0.6fr 1.4fr',padding:'3px 6px',gap:4,borderBottom:'1px solid rgba(255,255,255,0.06)',alignItems:'center'}}>
              <span style={{color:'#fff',fontWeight:600}}>{r.dest}</span>
              <span style={{color:'rgba(255,255,255,0.7)',fontVariantNumeric:'tabular-nums'}}>{r.time}</span>
              <span style={{color:'rgba(255,255,255,0.5)',textAlign:'center'}}>{r.plat}</span>
              <span style={{
                color:'#111',fontWeight:700,fontSize:'5.5px',letterSpacing:'0.04em',
                background:statusColor,padding:'1px 4px',borderRadius:2,textAlign:'center',
                display:'inline-block',overflow:'hidden',
                animation: isFlipping ? 'splitFlap 0.6s ease-in-out' : 'none',
              }}>{status}</span>
            </div>
          );
        })}
      </div>
    </TileShell>);
}
function TilePromo() {
  const [slide, setSlide] = useState(0);
  const [animating, setAnimating] = useState(false);
  const slides = [
    { big: '25% off', sub: 'selected outerwear' },
    { big: "Don't miss out!", sub: 'limited time offer' },
    { big: 'New arrivals', sub: 'shop the latest styles' },
  ];

  useEffect(() => {
    const t = setInterval(() => {
      setAnimating(true);
      setTimeout(() => {
        setSlide(s => (s + 1) % slides.length);
        setAnimating(false);
      }, 600);
    }, 3800);
    return () => clearInterval(t);
  }, []);

  const renderLine = (text, prefix, isExit) => {
    return text.split('').map((ch, i) => {
      const delay = i * 28;
      const base = {
        display: 'inline-block',
        transition: 'opacity 0.4s ease ' + delay + 'ms, transform 0.5s ease ' + delay + 'ms',
        whiteSpace: 'pre',
      };
      if (isExit) {
        Object.assign(base, {
          opacity: 0,
          transform: 'translateY(-12px) rotate(' + (i % 2 === 0 ? -8 : 8) + 'deg) scale(0.7)',
        });
      } else {
        Object.assign(base, {
          opacity: 1,
          transform: 'translateY(0) rotate(0deg) scale(1)',
        });
      }
      return React.createElement('span', { key: prefix + i, style: base }, ch === ' ' ? ' ' : ch);
    });
  };

  return (
    React.createElement(TileShell, { title: 'PROMO · retail', tone: 'accent' },
      React.createElement('div', { className: 't-promo', style: { position: 'relative', overflow: 'hidden' } },
        React.createElement('div', { className: 't-promo-big', style: { lineHeight: 1.1, minHeight: '1.2em' } },
          renderLine(slides[slide].big, 'b', animating)
        ),
        React.createElement('div', { className: 't-promo-sub', style: { minHeight: '1em' } },
          renderLine(slides[slide].sub, 's', animating)
        )
      )
    )
  );
}
function TileWeather() {
  const [newsIdx, setNewsIdx] = useState(0);
  const news = [
    { headline: 'New Metro Line Opens December', caption: 'Sydney CBD to Parramatta in 18 minutes.' },
    { headline: 'University Rankings Released', caption: 'Three Australian unis crack global top 30.' },
    { headline: 'Harbour Bridge Turns 100', caption: 'City prepares celebrations for the milestone.' },
    { headline: 'Tech Hub Expansion Approved', caption: '$2B innovation precinct for North Sydney.' },
  ];
  const illustrations = [
    <svg key="metro" viewBox="0 0 80 50" style={{position:'absolute',left:'50%',top:'45%',transform:'translate(-50%,-50%)',width:'80%',height:'80%',opacity:0.25}}><path d="M10 38h60M15 38v-14h50v14M22 24v-6h36v6M28 18v-4h24v4" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/><circle cx="25" cy="38" r="3" fill="none" stroke="#fff" strokeWidth="1.2"/><circle cx="55" cy="38" r="3" fill="none" stroke="#fff" strokeWidth="1.2"/><rect x="30" y="27" width="5" height="5" rx="1" fill="#fff" opacity="0.5"/><rect x="38" y="27" width="5" height="5" rx="1" fill="#fff" opacity="0.5"/><rect x="46" y="27" width="5" height="5" rx="1" fill="#fff" opacity="0.5"/></svg>,
    <svg key="uni" viewBox="0 0 80 50" style={{position:'absolute',left:'50%',top:'45%',transform:'translate(-50%,-50%)',width:'80%',height:'80%',opacity:0.25}}><path d="M40 8L10 22h60L40 8z" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinejoin="round"/><rect x="17" y="22" width="6" height="18" fill="none" stroke="#fff" strokeWidth="1"/><rect x="27" y="22" width="6" height="18" fill="none" stroke="#fff" strokeWidth="1"/><rect x="37" y="22" width="6" height="18" fill="none" stroke="#fff" strokeWidth="1"/><rect x="47" y="22" width="6" height="18" fill="none" stroke="#fff" strokeWidth="1"/><rect x="57" y="22" width="6" height="18" fill="none" stroke="#fff" strokeWidth="1"/><rect x="12" y="40" width="56" height="3" rx="0.5" fill="#fff" opacity="0.4"/><circle cx="40" cy="14" r="2" fill="#fff" opacity="0.5"/></svg>,
    <svg key="bridge" viewBox="0 0 80 50" style={{position:'absolute',left:'50%',top:'45%',transform:'translate(-50%,-50%)',width:'80%',height:'80%',opacity:0.25}}><path d="M4 35 Q20 10 40 10 Q60 10 76 35" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"/><line x1="4" y1="35" x2="76" y2="35" stroke="#fff" strokeWidth="1.5"/><line x1="15" y1="35" x2="15" y2="22" stroke="#fff" strokeWidth="0.8"/><line x1="25" y1="35" x2="25" y2="14" stroke="#fff" strokeWidth="0.8"/><line x1="35" y1="35" x2="35" y2="11" stroke="#fff" strokeWidth="0.8"/><line x1="45" y1="35" x2="45" y2="11" stroke="#fff" strokeWidth="0.8"/><line x1="55" y1="35" x2="55" y2="14" stroke="#fff" strokeWidth="0.8"/><line x1="65" y1="35" x2="65" y2="22" stroke="#fff" strokeWidth="0.8"/><rect x="10" y="35" width="10" height="8" fill="none" stroke="#fff" strokeWidth="0.6"/><rect x="60" y="35" width="10" height="8" fill="none" stroke="#fff" strokeWidth="0.6"/></svg>,
    <svg key="tech" viewBox="0 0 80 50" style={{position:'absolute',left:'50%',top:'45%',transform:'translate(-50%,-50%)',width:'80%',height:'80%',opacity:0.25}}><rect x="20" y="12" width="22" height="30" rx="2" fill="none" stroke="#fff" strokeWidth="1.2"/><rect x="38" y="18" width="22" height="24" rx="2" fill="none" stroke="#fff" strokeWidth="1.2"/>{[0,1,2,3].map(i=><rect key={i} x={24} y={16+i*6} width="5" height="3" rx="0.5" fill="#fff" opacity="0.35"/>)}{[0,1,2].map(i=><rect key={'b'+i} x={42} y={22+i*6} width="5" height="3" rx="0.5" fill="#fff" opacity="0.35"/>)}<circle cx="52" cy="38" r="1.5" fill="#fff" opacity="0.4"/><path d="M14 30h6M60 28h8M30 8v4M50 8v4" stroke="#fff" strokeWidth="0.8" strokeLinecap="round" opacity="0.3"/></svg>,
  ];
  const grads = [
    'linear-gradient(135deg,#1e3a5f,#2d5a8e)',
    'linear-gradient(135deg,#5b2c6f,#8e44ad)',
    'linear-gradient(135deg,#1a5c3a,#27ae60)',
    'linear-gradient(135deg,#7d4e00,#d4881f)',
  ];
  useEffect(() => {
    const t = setInterval(() => setNewsIdx(p => (p + 1) % news.length), 3500);
    return () => clearInterval(t);
  }, []);
  return (
    <TileShell title="WEATHER / NEWS · syd">
      <div style={{display:'flex',gap:6,width:'100%',height:'100%',alignItems:'stretch'}}>
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',minWidth:'36%'}}>
          <span className="t-temp">22°</span>
          <span className="t-w-sub">partly cloudy</span>
        </div>
        <div style={{flex:1,overflow:'hidden',position:'relative',borderRadius:4}}>
          {news.map((item, i) => (
            <div key={i} style={{
              position:'absolute',inset:0,display:'flex',flexDirection:'column',justifyContent:'flex-end',
              background:grads[i],borderRadius:4,padding:'5px 6px',overflow:'hidden',
              opacity:i===newsIdx?1:0,transform:i===newsIdx?'translateY(0)':'translateY(8px)',
              transition:'opacity 0.6s ease, transform 0.6s ease',
            }}>
              {illustrations[i]}
              <div style={{position:'relative',zIndex:1}}>
                <div style={{fontSize:'5px',fontWeight:800,color:'#fbbf24',letterSpacing:'0.08em',marginBottom:2}}>BREAKING</div>
                <div style={{fontSize:'6.5px',fontWeight:700,color:'#fff',lineHeight:1.25,marginBottom:2}}>{item.headline}</div>
                <div style={{fontSize:'5px',color:'rgba(255,255,255,0.75)',lineHeight:1.3}}>{item.caption}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </TileShell>);
}
function TileMenu() {
  return (
    <TileShell title="CARPARK · reservation">
      <div className="t-carpark">
        <div className="t-carpark-spot">
          <div className="t-carpark-badge">RESERVED</div>
          <div className="t-carpark-plate">
            <span className="t-plate-state">NSW</span>
            <span className="t-plate-num">DXB 42T</span>
          </div>
          <div className="t-carpark-bay">Bay 12 · Level P2</div>
        </div>
      </div>
    </TileShell>);
}
function TileStaff() {
  const doctors = [
    { initials: 'JS', name: 'Dr. J. Singh', color: '#3b82f6' },
    { initials: 'RP', name: 'Dr. R. Patel', color: '#6366f1' },
    { initials: 'LC', name: 'Dr. L. Chen', color: '#0ea5e9' },
  ];
  const allied = [
    { initials: 'KT', name: 'K. Taylor', color: '#10b981' },
    { initials: 'ML', name: 'M. Lee', color: '#14b8a6' },
    { initials: 'AW', name: 'A. Wilson', color: '#059669' },
    { initials: 'NR', name: 'N. Rivera', color: '#0d9488' },
  ];
  return (
    <TileShell title="STAFF · today">
      <div className="t-staff-cats">
        <div className="t-staff-cat">
          <div className="t-staff-cat-label">Doctors</div>
          <div className="t-staff-profiles">
            {doctors.map((d, i) => (
              <div key={i} className="t-staff-person">
                <div className="t-staff-avatar" style={{ background: d.color }}>{d.initials}</div>
                <span className="t-staff-name">{d.name}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="t-staff-cat">
          <div className="t-staff-cat-label">Allied Services</div>
          <div className="t-staff-profiles">
            {allied.map((d, i) => (
              <div key={i} className="t-staff-person">
                <div className="t-staff-avatar" style={{ background: d.color }}>{d.initials}</div>
                <span className="t-staff-name">{d.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </TileShell>);
}
function TileAlert() {
  const screens = [
    { name: 'Foyer', status: 'online' },
    { name: 'Level 1', status: 'online' },
    { name: 'Level 2', status: 'online' },
    { name: 'Waiting', status: 'online' },
    { name: 'Lobby', status: 'online' },
    { name: 'Ward A', status: 'online' },
  ];
  return (
    <TileShell title="MONITORING · live" tone="dark">
      <div className="t-monitor-grid">
        {screens.map((s, i) => (
          <div key={i} className="t-monitor-item">
            <div className="t-monitor-screen">
              <div className="t-monitor-screen-inner" />
            </div>
            <div className="t-monitor-meta">
              <span className={'t-monitor-dot t-monitor-' + s.status} />
              <span className="t-monitor-name">{s.name}</span>
            </div>
          </div>
        ))}
      </div>
    </TileShell>);
}

function PreviewDirectory() {
  const entries = [
    { floor: 'L1', name: 'Building Management',     suite: '101', lift: 'A', dir: 'left',  tag: null },
    { floor: 'L1', name: 'Westside Medical Centre',  suite: '103', lift: 'A', dir: 'left',  tag: { label: 'Temporary Closed', type: 'closed' } },
    { floor: 'L1', name: 'City Dental Group',        suite: '105', lift: 'B', dir: 'right', tag: null },
    { floor: 'L2', name: 'Allied Health & Physio',   suite: '201', lift: 'B', dir: 'right', tag: { label: 'Appointment Only', type: 'appt' } },
    { floor: 'L2', name: 'Specsavers Optometry',     suite: '204', lift: 'C', dir: 'left',  tag: null },
    { floor: 'L2', name: 'Skin & Cancer Foundation', suite: '207', lift: 'C', dir: 'left',  tag: { label: 'Closes at 3pm', type: 'early' } },
    { floor: 'L3', name: 'Commonwealth Bank',        suite: '301', lift: 'D', dir: 'right', tag: null },
    { floor: 'L3', name: 'Anytime Fitness',          suite: '310', lift: 'D', dir: 'right', tag: null },
    { floor: 'L4', name: 'Smith & Partners Legal',   suite: '401', lift: 'A', dir: 'left',  tag: null },
    { floor: 'L4', name: 'Rooftop Conference Ctr',   suite: '410', lift: 'B', dir: 'right', tag: null },
  ];
  const tagStyle = (type) => {
    if (type === 'closed') return { background: 'rgba(220,38,38,0.12)', color: '#c00', border: '1px solid rgba(220,38,38,0.35)', fontWeight:700 };
    if (type === 'appt')   return { background: 'rgba(0,102,255,0.1)',  color: '#0055cc', border: '1px solid rgba(0,102,255,0.3)', fontWeight:700 };
    if (type === 'early')  return { background: 'rgba(180,120,0,0.1)',  color: '#a06000', border: '1px solid rgba(180,120,0,0.35)', fontWeight:700 };
    return {};
  };
  const Arrow = ({ dir }) => (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{flexShrink:0}}>
      {dir === 'left'
        ? <path d="M8 5H2M2 5L5 2M2 5L5 8" stroke="#0066ff" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        : <path d="M2 5H8M8 5L5 2M8 5L5 8" stroke="#0066ff" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      }
    </svg>
  );
  return (
    <div style={{width:'100%',background:'#ffffff',borderRadius:6,overflow:'hidden',border:'1px solid #e5e7eb',fontFamily:'inherit'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'7px 10px',background:'#111',borderBottom:'2px solid #0066ff'}}>
        <div style={{display:'flex',alignItems:'center',gap:6}}>
          <div style={{width:18,height:18,borderRadius:3,background:'#0066ff',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
            <span style={{color:'#fff',fontSize:8,fontWeight:800,letterSpacing:'-0.5px'}}>NX</span>
          </div>
          <div style={{lineHeight:1}}>
            <div style={{fontSize:8,fontWeight:700,color:'#fff',letterSpacing:'0.04em'}}>NEXUS TOWER</div>
            <div style={{fontSize:6,color:'rgba(255,255,255,0.6)',letterSpacing:'0.06em'}}>BUILDING DIRECTORY</div>
          </div>
        </div>
        <div style={{textAlign:'center'}}>
          <div style={{fontSize:9,fontWeight:700,color:'#fff',letterSpacing:'0.08em'}}>DIRECTORY</div>
        </div>
        <div style={{textAlign:'right'}}>
          <div style={{fontSize:8,fontWeight:600,color:'#fff',fontVariantNumeric:'tabular-nums'}}>09:41</div>
          <div style={{fontSize:6,color:'rgba(255,255,255,0.6)'}}>MON 10 JUN 2026</div>
        </div>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'26px 1fr 38px 22px',gap:0,padding:'4px 10px 3px',background:'#f1f5f9',borderBottom:'1px solid #cbd5e1'}}>
        <span style={{fontSize:6,fontWeight:700,color:'#334155',letterSpacing:'0.06em'}}>LVL</span>
        <span style={{fontSize:6,fontWeight:700,color:'#334155',letterSpacing:'0.06em'}}>TENANT</span>
        <span style={{fontSize:6,fontWeight:700,color:'#334155',letterSpacing:'0.06em'}}>LIFT</span>
        <span style={{fontSize:6,fontWeight:700,color:'#334155',letterSpacing:'0.06em',textAlign:'right'}}>NO.</span>
      </div>
      <div>
        {entries.map((e, i) => (
          <div key={i} style={{display:'grid',gridTemplateColumns:'26px 1fr 38px 22px',gap:0,padding:'5px 10px',borderBottom:'1px solid #e5e7eb',background: i % 2 === 0 ? '#fff' : '#f8fafc',alignItems:'center'}}>
            <span style={{fontSize:7.5,color:'#0066ff',fontWeight:700}}>{e.floor}</span>
            <div style={{display:'flex',flexDirection:'column',gap:2}}>
              <span style={{fontSize:7.5,color:'#111',fontWeight:500,lineHeight:1.2}}>{e.name}</span>
              {e.tag && (
                <span style={{fontSize:6,padding:'1px 5px',borderRadius:3,display:'inline-block',width:'fit-content',letterSpacing:'0.03em',...tagStyle(e.tag.type)}}>
                  {e.tag.label}
                </span>
              )}
            </div>
            <div style={{display:'flex',alignItems:'center',gap:3}}>
              <Arrow dir={e.dir} />
              <span style={{fontSize:7,color:'#111',fontWeight:600}}>Lift {e.lift}</span>
            </div>
            <span style={{fontSize:7,color:'#334155',textAlign:'right',fontWeight:500}}>{e.suite}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PreviewCustomSignage() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = 3;
  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setActive(p => (p + 1) % total), 3000);
    return () => clearInterval(t);
  }, [paused]);

  const Poster = () => (
    <div style={{width:'100%',height:'100%',background:'linear-gradient(135deg,#0a0a2e 0%,#111144 40%,#0a0a2e 100%)',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:16,textAlign:'center',position:'relative',overflow:'hidden'}}>
      <div style={{position:'absolute',top:0,left:0,right:0,bottom:0,background:'radial-gradient(ellipse at 50% 30%, rgba(0,102,255,0.15) 0%, transparent 60%)'}} />
      <div style={{position:'absolute',top:0,left:0,right:0,bottom:0,opacity:0.04,backgroundImage:'repeating-linear-gradient(0deg,transparent,transparent 8px,rgba(255,255,255,0.5) 8px,rgba(255,255,255,0.5) 9px),repeating-linear-gradient(90deg,transparent,transparent 8px,rgba(255,255,255,0.5) 8px,rgba(255,255,255,0.5) 9px)'}} />
      <div style={{position:'absolute',top:8,left:10,right:10,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div style={{display:'flex',alignItems:'center',gap:4}}>
          <div style={{width:14,height:14,borderRadius:3,background:'#0066ff',display:'flex',alignItems:'center',justifyContent:'center'}}><span style={{color:'#fff',fontSize:6,fontWeight:800}}>NX</span></div>
          <span style={{fontSize:5.5,fontWeight:600,color:'rgba(255,255,255,0.5)',letterSpacing:'0.06em'}}>NEXUS TOWER</span>
        </div>
        <span style={{fontSize:5,color:'rgba(255,255,255,0.35)',letterSpacing:'0.06em'}}>LEVEL 4 ROOFTOP</span>
      </div>
      <div style={{position:'relative',zIndex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:0}}>
        <div style={{fontSize:8,fontWeight:700,color:'#60a5fa',letterSpacing:'0.16em',marginBottom:12,textTransform:'uppercase',background:'rgba(0,102,255,0.15)',padding:'3px 12px',borderRadius:10}}>You're Invited</div>
        <div style={{width:60,height:0.5,background:'linear-gradient(90deg,transparent,#0066ff,transparent)',marginBottom:12}} />
        <div style={{fontSize:28,fontWeight:800,color:'#fff',lineHeight:1.05,marginBottom:4,letterSpacing:'-0.03em'}}>Winter</div>
        <div style={{fontSize:28,fontWeight:800,color:'#0066ff',lineHeight:1.05,marginBottom:10,letterSpacing:'-0.03em'}}>Gala 2026</div>
        <div style={{width:60,height:0.5,background:'linear-gradient(90deg,transparent,rgba(255,255,255,0.2),transparent)',marginBottom:10}} />
        <div style={{fontSize:11,color:'rgba(255,255,255,0.8)',lineHeight:1.6,marginBottom:10,fontWeight:500}}>
          <div>Saturday, 18 July</div>
          <div style={{fontSize:14,color:'#fff',fontWeight:700}}>6:30 PM</div>
        </div>
        <div style={{fontSize:11,color:'#fff',background:'#0066ff',padding:'6px 22px',borderRadius:16,fontWeight:700,letterSpacing:'0.05em',boxShadow:'0 2px 12px rgba(0,102,255,0.4)'}}>RSVP NOW</div>
        <div style={{marginTop:12,display:'flex',gap:10,fontSize:8,color:'rgba(255,255,255,0.4)'}}>
          <span>Smart casual</span><span>·</span><span>Complimentary drinks</span><span>·</span><span>Live music</span>
        </div>
      </div>
      <div style={{position:'absolute',bottom:8,left:10,right:10,display:'flex',justifyContent:'space-between',fontSize:4.5,color:'rgba(255,255,255,0.25)'}}>
        <span>nexustower.com.au/events</span>
        <span>Powered by Advertise Me</span>
      </div>
    </div>
  );

  const Carpark = () => {
    const rowA = [
      {id:'A01',s:'taken'},{id:'A02',s:'free'},{id:'A03',s:'reserved',plate:'DKR 441'},{id:'A04',s:'taken'},{id:'A05',s:'free'},{id:'A06',s:'taken'},{id:'A07',s:'free'},{id:'A08',s:'taken'},
    ];
    const rowB = [
      {id:'B01',s:'taken'},{id:'B02',s:'free'},{id:'B03',s:'taken'},{id:'B04',s:'reserved',plate:'PLT 887'},{id:'B05',s:'free'},{id:'B06',s:'taken'},{id:'B07',s:'free'},{id:'B08',s:'free'},
    ];
    const rowC = [
      {id:'C01',s:'taken'},{id:'C02',s:'taken'},{id:'C03',s:'free'},{id:'C04',s:'taken'},{id:'C05',s:'reserved',plate:'VIP 001'},{id:'C06',s:'free'},{id:'C07',s:'taken'},{id:'C08',s:'taken'},
    ];
    const fill = (s) => s==='taken'?'#ef4444':s==='reserved'?'#f59e0b':'#22c55e';
    const Spot = ({sp,x,y,openSide}) => {
      const w=19,h=13;
      let lines='';
      if(openSide==='top'){lines='M0,'+h+'L0,0L'+w+',0L'+w+','+h;}
      else{lines='M0,0L0,'+h+'L'+w+','+h+'L'+w+',0';}
      return (
        <g transform={'translate('+x+','+y+')'}>
          <path d={lines} fill="none" stroke="#fff" strokeWidth="0.6" opacity="0.8"/>
          <rect x="0.5" y="0.5" width={w-1} height={h-1} rx="0.5" fill={fill(sp.s)} opacity="0.15"/>
          {sp.s==='taken' && <><rect x="4" y="2" width="11" height="8" rx="1.5" fill={fill(sp.s)} opacity="0.45"/><rect x="5.5" y="3.5" width="8" height="5" rx="0.8" fill={fill(sp.s)} opacity="0.6"/><circle cx="5" cy="4" r="1" fill={fill(sp.s)} opacity="0.5"/><circle cx="14" cy="4" r="1" fill={fill(sp.s)} opacity="0.5"/></>}
          {sp.s==='reserved' && <><rect x="2" y="2" width="15" height="8" rx="1" fill="none" stroke={fill(sp.s)} strokeWidth="0.5" strokeDasharray="1.5 1"/><text x="9.5" y="7.5" textAnchor="middle" fill={fill(sp.s)} fontSize="2.5" fontWeight="700">{sp.plate||'RSVD'}</text></>}
          {sp.s==='free' && <text x="9.5" y="8" textAnchor="middle" fill="#22c55e" fontSize="2.8" fontWeight="600">OPEN</text>}
          <text x="9.5" y={openSide==='top'?h+3:-1.5} textAnchor="middle" fill="#555" fontSize="2" fontWeight="600">{sp.id}</text>
        </g>
      );
    };
    const SpotRow = ({spots,y,openSide}) => spots.map((sp,i) => {
      const x = 14 + i * 22;
      return <Spot key={sp.id} sp={sp} x={x} y={y} openSide={openSide} />;
    });
    return (
      <div style={{width:'100%',height:'100%',background:'#e8e8e8',display:'flex',flexDirection:'column',overflow:'hidden'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'5px 10px',background:'#111',borderBottom:'2px solid #0066ff'}}>
          <span style={{fontSize:7,fontWeight:700,color:'#fff',letterSpacing:'0.06em'}}>CARPARK · LEVEL B1</span>
          <div style={{display:'flex',gap:6}}>
            {[['Taken','#ef4444'],['Reserved','#f59e0b'],['Open','#22c55e']].map(([l,cl])=>(
              <div key={l} style={{display:'flex',alignItems:'center',gap:2}}>
                <div style={{width:5,height:5,borderRadius:1,background:cl}} />
                <span style={{fontSize:5,color:'#fff',fontWeight:600}}>{l}</span>
              </div>
            ))}
          </div>
          <span style={{fontSize:7,fontWeight:600,color:'#0066ff'}}>9 / 24 Open</span>
        </div>
        <div style={{flex:1,position:'relative'}}>
          <svg viewBox="0 0 200 105" width="100%" height="100%" style={{display:'block'}}>
            <rect width="200" height="105" fill="#c8c8c8"/>
            <defs>
              <pattern id="concrete" width="6" height="6" patternUnits="userSpaceOnUse">
                <circle cx="1" cy="1" r="0.3" fill="#aaa" opacity="0.5"/>
                <circle cx="4" cy="3" r="0.2" fill="#999" opacity="0.4"/>
                <circle cx="2" cy="5" r="0.25" fill="#bbb" opacity="0.3"/>
              </pattern>
            </defs>
            <rect x="0" y="0" width="200" height="105" fill="url(#concrete)" opacity="0.4"/>
            <rect x="10" y="33" width="182" height="12" fill="#888" opacity="0.08" rx="0.5"/>
            <path d="M16 39 L184 39" stroke="#fff" strokeWidth="0.3" strokeDasharray="3 2" opacity="0.4"/>
            <rect x="10" y="67" width="182" height="12" fill="#888" opacity="0.08" rx="0.5"/>
            <path d="M16 73 L184 73" stroke="#fff" strokeWidth="0.3" strokeDasharray="3 2" opacity="0.4"/>
            <rect x="4" y="16" width="3.5" height="14" rx="1" fill="#aaa" opacity="0.5" stroke="#999" strokeWidth="0.3"/>
            <rect x="4" y="82" width="3.5" height="14" rx="1" fill="#aaa" opacity="0.5" stroke="#999" strokeWidth="0.3"/>
            <rect x="193" y="16" width="3.5" height="14" rx="1" fill="#aaa" opacity="0.5" stroke="#999" strokeWidth="0.3"/>
            <rect x="193" y="82" width="3.5" height="14" rx="1" fill="#aaa" opacity="0.5" stroke="#999" strokeWidth="0.3"/>
            <SpotRow spots={rowA} y={17} openSide="top"/>
            <SpotRow spots={rowB} y={47} openSide="top"/>
            <SpotRow spots={rowC} y={82} openSide="bottom"/>
            <path d="M14 7 L14 4 L186 4 L186 7" stroke="#f59e0b" strokeWidth="0.6" fill="none" opacity="0.5"/>
            <text x="100" y="6" textAnchor="middle" fill="#f59e0b" fontSize="2.5" fontWeight="700" opacity="0.6">EXIT TO STREET ↑</text>
            <text x="100" y="102" textAnchor="middle" fill="#777" fontSize="2.5" fontWeight="600" opacity="0.5">↓ LEVEL B2</text>
          </svg>
        </div>
      </div>
    );
  };

  const Social = () => {
    const posts = [
      {handle:'@nexustower',text:'Sunset views from our rooftop terrace tonight.',color:'#E1306C',icon:'IG',likes:'2.4k',img:'linear-gradient(135deg,#ff9a56,#ff2d87)',h:2},
      {handle:'Nexus Tower',text:'98% tenant occupancy. Thank you partners.',color:'#0077B5',icon:'in',likes:'847',img:'linear-gradient(135deg,#0077B5,#00a0dc)',h:1},
      {handle:'@nexus_tower',text:'New cafe Level 1 Friday! 50 free coffees.',color:'#000',icon:'X',likes:'312',img:null,h:1},
      {handle:'Nexus Tower',text:'Yoga in the Park every Wednesday 7am.',color:'#1877F2',icon:'f',likes:'1.1k',img:'linear-gradient(135deg,#43e97b,#38f9d7)',h:1},
      {handle:'@nexustower',text:'Lobby art installation by @studio.raw',color:'#E1306C',icon:'IG',likes:'3.1k',img:'linear-gradient(135deg,#a18cd1,#fbc2eb)',h:2},
      {handle:'@nexustower',text:'The elevator race challenge!',color:'#000',icon:'TT',likes:'14k',img:'linear-gradient(135deg,#00f2ea,#ff0050)',h:1},
      {handle:'Nexus Tower',text:'Congrats Skin & Cancer Foundation.',color:'#1877F2',icon:'f',likes:'628',img:null,h:1},
      {handle:'@nexustower',text:'Winter Gala tickets selling fast.',color:'#E1306C',icon:'IG',likes:'956',img:'linear-gradient(135deg,#0a0a2e,#0066ff)',h:1},
      {handle:'@nexustower',text:'Morning light through the atrium.',color:'#E1306C',icon:'IG',likes:'4.2k',img:'linear-gradient(135deg,#ffecd2,#fcb69f)',h:2},
      {handle:'Nexus Tower',text:'Welcome City Dental Group!',color:'#0077B5',icon:'in',likes:'1.3k',img:'linear-gradient(135deg,#667eea,#764ba2)',h:1},
      {handle:'@nexus_tower',text:'Last year gala throwback.',color:'#000',icon:'X',likes:'189',img:'linear-gradient(135deg,#2c3e50,#fd746c)',h:1},
      {handle:'Nexus Tower',text:'Free fruit Fridays in the lobby!',color:'#1877F2',icon:'f',likes:'742',img:'linear-gradient(135deg,#f7971e,#ffd200)',h:1},
      {handle:'@nexustower',text:'Christmas lights on the building.',color:'#E1306C',icon:'IG',likes:'5.8k',img:'linear-gradient(135deg,#c31432,#240b36)',h:2},
      {handle:'@nexustower',text:'Dance class Thursdays 6pm!',color:'#000',icon:'TT',likes:'8.2k',img:'linear-gradient(135deg,#ee9ca7,#ffdde1)',h:1},
      {handle:'Nexus Tower',text:'Maintenance Saturday 6am-12pm.',color:'#0077B5',icon:'in',likes:'94',img:null,h:1},
      {handle:'Nexus Tower',text:'Happy Easter from the team!',color:'#1877F2',icon:'f',likes:'1.8k',img:'linear-gradient(135deg,#a8ff78,#78ffd6)',h:1},
    ];
    return (
      <div style={{width:'100%',height:'100%',background:'#f1f5f9',display:'flex',flexDirection:'column',overflow:'hidden'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'5px 10px',background:'#111',borderBottom:'2px solid #0066ff'}}>
          <span style={{fontSize:7,fontWeight:700,color:'#fff',letterSpacing:'0.06em'}}>SOCIAL WALL</span>
          <div style={{display:'flex',gap:3}}>
            {[['IG','#E1306C'],['in','#0077B5'],['X','#000'],['f','#1877F2'],['TT','#ff0050']].map(([ic,cl])=>(
              <div key={ic} style={{width:9,height:9,borderRadius:5,background:cl,display:'flex',alignItems:'center',justifyContent:'center'}}>
                <span style={{color:'#fff',fontSize:4.5,fontWeight:800}}>{ic}</span>
              </div>
            ))}
          </div>
          <span style={{fontSize:6,color:'rgba(255,255,255,0.4)'}}>Live · 16 posts</span>
        </div>
        <div style={{flex:1,display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gridAutoRows:'1fr',gap:2.5,padding:3,overflow:'hidden'}}>
          {posts.map((p,i)=>(
            <div key={i} style={{background:'#fff',borderRadius:3,overflow:'hidden',border:'1px solid #e2e8f0',gridRow:p.h===2?'span 2':'span 1',display:'flex',flexDirection:'column'}}>
              {p.img && <div style={{flex:1,minHeight:0,background:p.img,position:'relative'}}>
                <div style={{position:'absolute',bottom:1.5,right:1.5,background:'rgba(0,0,0,0.55)',borderRadius:2,padding:'0.5px 3px',fontSize:3.5,color:'#fff',fontWeight:600}}>♥ {p.likes}</div>
              </div>}
              <div style={{padding:'2.5px 3px',flexShrink:0,display:'flex',flexDirection:'column',gap:1.5}}>
                <div style={{display:'flex',alignItems:'center',gap:2}}>
                  <div style={{width:8,height:8,borderRadius:4,background:p.color,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                    <span style={{color:'#fff',fontSize:4,fontWeight:800}}>{p.icon}</span>
                  </div>
                  <span style={{fontSize:4,fontWeight:700,color:'#111',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{p.handle}</span>
                </div>
                <div style={{fontSize:4,color:'#334155',lineHeight:1.3}}>{p.text}</div>
                {!p.img && <div style={{fontSize:3.5,color:'#94a3b8'}}>♥ {p.likes}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div
      style={{width:'100%',borderRadius:6,overflow:'hidden',border:'1px solid #e5e7eb',background:'#fff',position:'relative',aspectRatio:'16/10'}}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div style={{display:'flex',width:total*100+'%',transform:'translateX(-'+(active*(100/total))+'%)',transition:'transform 0.8s cubic-bezier(0.4,0,0.2,1)',height:'100%'}}>
        <div style={{width:100/total+'%',flexShrink:0,height:'100%'}}><Poster /></div>
        <div style={{width:100/total+'%',flexShrink:0,height:'100%'}}><Carpark /></div>
        <div style={{width:100/total+'%',flexShrink:0,height:'100%'}}><Social /></div>
      </div>
      <div style={{position:'absolute',bottom:4,left:'50%',transform:'translateX(-50%)',display:'flex',gap:4}}>
        {[0,1,2].map(i=>(
          <div key={i} style={{width:i===active?12:5,height:3,borderRadius:2,background:i===active?'#0066ff':'rgba(0,0,0,0.2)',transition:'all 0.4s ease'}} />
        ))}
      </div>
    </div>
  );
}




// ---------------- ProductPreview (animated screen mock for product cards) -----------------
function ProductPreview({ slug }) {
  if (slug === 'interactive-touch/information-kiosks') return <PreviewKiosk />;
  if (slug === 'interactive-touch/digital-wayfinding') return <PreviewKiosk />;
  if (slug === 'digital-signage/medical-centres') return <PreviewSignage />;
  if (slug === 'digital-signage/bed-management') return <PreviewBeds />;
  if (slug === 'interactive-touch/eposters') return <PreviewEPoster />;
  if (slug === 'digital-signage/directory-boards') return <PreviewDirectory />;
  if (slug === 'interactive-touch/directory-boards') return <PreviewDirectory />;
  if (slug === 'digital-signage/custom') return <PreviewCustomSignage />;
  return null;
}
function PreviewKiosk() {
  const [time, setTime] = useState('09:41');
  useEffect(() => {
    const fmt = () => { const d = new Date(); setTime(String(d.getHours()).padStart(2,'0')+':'+String(d.getMinutes()).padStart(2,'0')); };
    fmt();
    const t = setInterval(fmt, 10000);
    return () => clearInterval(t);
  }, []);

  const navItems = [
    { label: 'Search', icon: 'M7 7a3 3 0 1 1 4.2 4.2M11 11l3 3', active: false },
    { label: 'Information', icon: 'M8 5h.01M8 8v3', active: true },
    { label: 'News', icon: 'M4 5h8v9H4zM6 8h4M6 11h4', active: false },
    { label: 'Events', icon: 'M5 6h8v8H5zM5 9h8M7 4v3M11 4v3', active: false },
  ];
  const a11y = [
    { label: 'Language', icon: 'M8 3a5 5 0 1 0 0 10A5 5 0 0 0 8 3zM3 8h10M8 3c1.5 2 1.5 8 0 10M8 3c-1.5 2-1.5 8 0 10' },
    { label: 'Toilets', icon: 'M5 4a1 1 0 1 0 0-.01M5 6v4M5 10l-1 3M5 10l1 3M11 4a1 1 0 1 0 0-.01M11 6l-1.5 4h3L11 6zM9.5 10v3M12.5 10v3' },
    { label: 'Magnify', icon: 'M7 7a3 3 0 1 1 4.2 4.2M11 11l3 3' },
  ];

  const Icon = ({x,y,label,color}) => (
    <g transform={'translate('+x+','+y+')'}>
      <circle r="4.5" fill="#fff" stroke={color} strokeWidth="0.8"/>
      <text y="1.5" textAnchor="middle" fill={color} fontSize="4" fontWeight="800">{label}</text>
    </g>
  );

  return (
    <div style={{width:'100%',borderRadius:6,overflow:'hidden',border:'1px solid #e5e7eb',background:'#fff',position:'relative',aspectRatio:'16/10',display:'flex',flexDirection:'column'}}>
      <style>{'@keyframes kioskDash{to{stroke-dashoffset:-100}}@keyframes kioskPulse{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.18);opacity:0.6}}@keyframes kioskBob{0%,100%{transform:translateY(0)}50%{transform:translateY(-1.5px)}}'}</style>

      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'5px 12px',background:'#fff',borderBottom:'1px solid #eef0f3',flexShrink:0}}>
        <div style={{display:'flex',alignItems:'center',gap:5}}>
          <div style={{width:16,height:16,borderRadius:4,background:'#3b82f6',display:'flex',alignItems:'center',justifyContent:'center'}}><span style={{color:'#fff',fontSize:7,fontWeight:800}}>MQ</span></div>
          <span style={{fontSize:7,fontWeight:700,color:'#1f2937',letterSpacing:'0.02em'}}>Macquarie University</span>
        </div>
        <span style={{fontSize:8,fontWeight:600,color:'#475569',letterSpacing:'0.08em'}}>INFORMATION KIOSK</span>
        <span style={{fontSize:7.5,fontWeight:600,color:'#3b82f6',fontVariantNumeric:'tabular-nums'}}>{time} · Mon 10 Jun</span>
      </div>

      <div style={{flex:1,display:'flex',minHeight:0}}>
        <div style={{width:'22%',background:'#f8fafc',borderRight:'1px solid #eef0f3',display:'flex',flexDirection:'column',padding:'6px 5px',gap:3}}>
          {navItems.map((n) => (
            <div key={n.label} style={{display:'flex',alignItems:'center',gap:5,padding:'5px 6px',borderRadius:5,background:n.active?'#e3f2fd':'transparent'}}>
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{flexShrink:0}}><path d={n.icon} stroke={n.active?'#3b82f6':'#94a3b8'} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
              <span style={{fontSize:7,fontWeight:600,color:n.active?'#3b82f6':'#64748b'}}>{n.label}</span>
            </div>
          ))}
          <div style={{flex:1}} />
          <div style={{height:1,background:'#eef0f3',margin:'2px 4px'}} />
          {a11y.map((n) => (
            <div key={n.label} style={{display:'flex',alignItems:'center',gap:5,padding:'4px 6px',borderRadius:5}}>
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" style={{flexShrink:0}}><path d={n.icon} stroke="#10b981" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              <span style={{fontSize:6.5,fontWeight:500,color:'#64748b'}}>{n.label}</span>
            </div>
          ))}
        </div>

        <div style={{flex:1,position:'relative',background:'#fff',overflow:'hidden'}}>
          <svg viewBox="0 0 260 150" width="100%" height="100%" style={{position:'absolute',inset:0,display:'block'}}>
            <rect width="260" height="150" fill="#fbfcfe"/>

            <rect x="116" y="14" width="32" height="124" rx="3" fill="#e8f5e9"/>
            <circle cx="132" cy="38" r="4" fill="#a5d6a7"/><circle cx="125" cy="50" r="3" fill="#81c784"/><circle cx="140" cy="56" r="3.2" fill="#a5d6a7"/>
            <circle cx="130" cy="102" r="4" fill="#a5d6a7"/><circle cx="139" cy="116" r="3" fill="#81c784"/><circle cx="124" cy="122" r="3.2" fill="#a5d6a7"/>
            <path d="M132 64 Q126 78 132 92" stroke="#c8e6c9" strokeWidth="1.5" fill="none"/>
            <text transform="translate(134,96) rotate(-90)" textAnchor="middle" fill="#66bb6a" fontSize="5.5" fontWeight="700" letterSpacing="0.05em">GIBSON PARK</text>

            <rect x="6" y="14" width="110" height="124" rx="4" fill="#ffffff" stroke="#b0bec5" strokeWidth="0.7"/>
            <rect x="6.6" y="14.6" width="108.8" height="11" rx="3.4" fill="#eceff1"/>
            <text x="61" y="22.5" textAnchor="middle" fill="#455a64" fontSize="7" fontWeight="800" letterSpacing="0.04em">BUILDING A</text>

            <rect x="10" y="28" width="34" height="44" rx="2" fill="#ffffff" stroke="#cfd8dc" strokeWidth="0.5"/>
            <text x="27" y="48" textAnchor="middle" fill="#455a64" fontSize="5" fontWeight="600">Lecture A1</text><text x="27" y="55" textAnchor="middle" fill="#78909c" fontSize="4">Rm 1.01</text>
            <rect x="46" y="28" width="34" height="44" rx="2" fill="#ffffff" stroke="#cfd8dc" strokeWidth="0.5"/>
            <text x="63" y="48" textAnchor="middle" fill="#455a64" fontSize="5" fontWeight="600">Lecture A2</text><text x="63" y="55" textAnchor="middle" fill="#78909c" fontSize="4">Rm 1.02</text>
            <rect x="82" y="28" width="30" height="44" rx="2" fill="#ffffff" stroke="#cfd8dc" strokeWidth="0.5"/>
            <text x="97" y="48" textAnchor="middle" fill="#455a64" fontSize="5" fontWeight="600">Study Hub</text><text x="97" y="55" textAnchor="middle" fill="#78909c" fontSize="4">Rm 1.03</text>

            <rect x="10" y="73" width="102" height="9" fill="#eef2f6"/>
            <line x1="14" y1="77.5" x2="108" y2="77.5" stroke="#cfd8dc" strokeWidth="0.4" strokeDasharray="2 2"/>

            <rect x="10" y="83" width="40" height="51" rx="2" fill="#ffffff" stroke="#cfd8dc" strokeWidth="0.5"/>
            <text x="30" y="100" textAnchor="middle" fill="#455a64" fontSize="5" fontWeight="600">Main Lobby</text>
            <rect x="52" y="83" width="30" height="51" rx="2" fill="#ffffff" stroke="#cfd8dc" strokeWidth="0.5"/>
            <text x="67" y="106" textAnchor="middle" fill="#455a64" fontSize="5" fontWeight="600">Admin</text><text x="67" y="113" textAnchor="middle" fill="#78909c" fontSize="4">Rm 1.04</text>
            <rect x="84" y="83" width="28" height="51" rx="2" fill="#ffffff" stroke="#cfd8dc" strokeWidth="0.5"/>
            <text x="98" y="108" textAnchor="middle" fill="#455a64" fontSize="5" fontWeight="600">Café</text>

            <Icon x={16} y={77.5} label="WC" color="#5b8def"/>
            <Icon x={28} y={77.5} label="L" color="#f59e0b"/>

            <rect x="148" y="14" width="106" height="124" rx="4" fill="#ffffff" stroke="#b0bec5" strokeWidth="0.7"/>
            <rect x="148.6" y="14.6" width="104.8" height="11" rx="3.4" fill="#eceff1"/>
            <text x="201" y="22.5" textAnchor="middle" fill="#455a64" fontSize="7" fontWeight="800" letterSpacing="0.04em">BUILDING B</text>

            <rect x="152" y="28" width="32" height="44" rx="2" fill="#ffffff" stroke="#cfd8dc" strokeWidth="0.5"/>
            <text x="168" y="48" textAnchor="middle" fill="#455a64" fontSize="5" fontWeight="600">Lab B1</text><text x="168" y="55" textAnchor="middle" fill="#78909c" fontSize="4">Rm 2.01</text>
            <rect x="186" y="28" width="32" height="44" rx="2" fill="#ffffff" stroke="#cfd8dc" strokeWidth="0.5"/>
            <text x="202" y="48" textAnchor="middle" fill="#455a64" fontSize="5" fontWeight="600">Lab B2</text><text x="202" y="55" textAnchor="middle" fill="#78909c" fontSize="4">Rm 2.02</text>
            <rect x="220" y="28" width="30" height="44" rx="2" fill="#ffffff" stroke="#cfd8dc" strokeWidth="0.5"/>
            <text x="235" y="48" textAnchor="middle" fill="#455a64" fontSize="5" fontWeight="600">Lecture B1</text><text x="235" y="55" textAnchor="middle" fill="#78909c" fontSize="4">Rm 2.03</text>

            <rect x="152" y="73" width="98" height="9" fill="#eef2f6"/>
            <line x1="156" y1="77.5" x2="246" y2="77.5" stroke="#cfd8dc" strokeWidth="0.4" strokeDasharray="2 2"/>

            <rect x="152" y="83" width="42" height="51" rx="2" fill="#ffffff" stroke="#cfd8dc" strokeWidth="0.5"/>
            <text x="173" y="98" textAnchor="middle" fill="#455a64" fontSize="5" fontWeight="600">Seminar Room</text><text x="173" y="105" textAnchor="middle" fill="#78909c" fontSize="4">Rm 2.04</text>
            <rect x="196" y="83" width="54" height="51" rx="2" fill="#ffffff" stroke="#cfd8dc" strokeWidth="0.5"/>
            <text x="223" y="110" textAnchor="middle" fill="#455a64" fontSize="5" fontWeight="600">Library</text>

            <Icon x={232} y={77.5} label="L" color="#f59e0b"/>
            <Icon x={242} y={77.5} label="WC" color="#5b8def"/>

            <rect x="116" y="73" width="32" height="9" fill="#eef2f6" stroke="#cfd8dc" strokeWidth="0.4"/>

            <path d="M30 114 L30 77.5 L173 77.5 L173 108" stroke="#3b82f6" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="6 4" style={{animation:'kioskDash 2s linear infinite'}}/>

            <g style={{transformOrigin:'173px 108px',animation:'kioskPulse 2s ease-in-out infinite 0.4s'}}><circle cx="173" cy="108" r="6" fill="#14b8a6" opacity="0.25"/></g>
            <circle cx="173" cy="108" r="2.8" fill="#14b8a6" stroke="#fff" strokeWidth="1"/>
            <rect x="150" y="89" width="46" height="11" rx="3" fill="#fff" stroke="#14b8a6" strokeWidth="0.8"/>
            <path d="M170 100 L173 104 L176 100 Z" fill="#fff" stroke="#14b8a6" strokeWidth="0.6"/>
            <text x="173" y="96.5" textAnchor="middle" fill="#14b8a6" fontSize="5.5" fontWeight="700">DESTINATION</text>

            <g style={{animation:'kioskBob 2.5s ease-in-out infinite'}}>
              <g style={{transformOrigin:'30px 114px',animation:'kioskPulse 2s ease-in-out infinite'}}><circle cx="30" cy="114" r="9" fill="#3b82f6" opacity="0.2"/></g>
              <circle cx="30" cy="114" r="3.5" fill="#3b82f6" stroke="#fff" strokeWidth="1.5"/>
              <rect x="9" y="101" width="42" height="11" rx="3" fill="#3b82f6"/>
              <path d="M27 112 L30 116 L33 112 Z" fill="#3b82f6"/>
              <text x="30" y="108.5" textAnchor="middle" fill="#fff" fontSize="5.5" fontWeight="700">YOU ARE HERE</text>
            </g>
          </svg>

          <div style={{position:'absolute',bottom:6,left:8,background:'#fff',border:'1px solid #e5e7eb',borderRadius:5,padding:'4px 9px',display:'flex',alignItems:'center',gap:6,boxShadow:'0 1px 4px rgba(0,0,0,0.06)'}}>
            <span style={{fontSize:6.5,color:'#1f2937',fontWeight:700}}>Route to Seminar Room 2.04</span>
            <span style={{fontSize:6,color:'#3b82f6',fontWeight:600}}>· Building B · 4 min</span>
          </div>
        </div>
      </div>
    </div>
  );
}


function PreviewSignage() {
  const [front, setFront] = useState(0);
  const total = 3;

  useEffect(() => {
    const t = setInterval(() => setFront(f => (f + 1) % total), 3500);
    return () => clearInterval(t);
  }, []);

  const getStyle = (i) => {
    const pos = (i - front + total) % total;
    if (pos === 0) return {
      zIndex: 3,
      width: 'calc(100% - 80px)',
      left: 0,
      transform: 'translateY(0px)',
      opacity: 1,
      boxShadow: '0 8px 32px rgba(0,0,0,0.14)'
    };
    if (pos === 1) return {
      zIndex: 2,
      width: 'calc(100% - 80px)',
      left: '48px',
      transform: 'translateY(10px)',
      opacity: 0.7,
      boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
    };
    return {
      zIndex: 1,
      width: 'calc(100% - 80px)',
      left: '80px',
      transform: 'translateY(20px)',
      opacity: 0.4,
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
    };
  };

  const card1 = (
    <div className="prev-screen">
      <div className="prev-screen-top">
        <span className="mono">CLINIC · MONDAY</span>
        <span className="mono pulse-dot">● LIVE</span>
      </div>
      <table className="prev-roster">
        <tbody>
          {[{n:'Dr. Singh',r:'Room 4',s:'In'},{n:'Dr. Lee',r:'Room 6',s:'In'},{n:'Dr. Patel',r:'Room 2',s:'Out'},{n:'Dr. Owens',r:'Room 5',s:'In'},{n:'Dr. Tran',r:'Room 3',s:'In'}].map((d,i) =>
            <tr key={i}>
              <td>{d.n}</td>
              <td>{d.r}</td>
              <td><span className={`pill ${d.s === 'In' ? 'pill--on' : 'pill--off'}`}>{d.s}</span></td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const avatars = [
    { name: 'Dr. Singh', gender: 'm' },
    { name: 'Dr. Lee', gender: 'f' },
    { name: 'Dr. Owens', gender: 'm' },
    { name: 'Dr. Tran', gender: 'f' },
    { name: 'Dr. Park', gender: 'f' },
    { name: 'Dr. Chen', gender: 'm' },
  ];
  const card2 = (
    <div className="prev-screen">
      <div className="prev-screen-top">
        <span className="mono">WHO'S IN TODAY</span>
        <span className="mono pulse-dot">● LIVE</span>
      </div>
      <div className="prev-avatar-grid">
        {avatars.map((a, i) => (
          <div key={i} className="prev-avatar-item">
            <div className={`prev-avatar prev-avatar--${a.gender}`}>
              {a.gender === 'f' ? (
                <svg viewBox="0 0 40 40" width="36" height="36" fill="none">
                  <circle cx="20" cy="15" r="7" fill="currentColor" opacity="0.7"/>
                  <path d="M6 38c0-7.732 6.268-14 14-14s14 6.268 14 14" stroke="currentColor" strokeWidth="2" fill="none" opacity="0.5"/>
                  <path d="M16 13c0-1 .5-3 4-3s4 2 4 3" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.4"/>
                </svg>
              ) : (
                <svg viewBox="0 0 40 40" width="36" height="36" fill="none">
                  <circle cx="20" cy="15" r="7" fill="currentColor" opacity="0.7"/>
                  <path d="M6 38c0-7.732 6.268-14 14-14s14 6.268 14 14" stroke="currentColor" strokeWidth="2" fill="none" opacity="0.5"/>
                </svg>
              )}
            </div>
            <span className="prev-avatar-name">{a.name.replace('Dr. ', '')}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const times = [
    { n: 'Dr. Singh', s: 'on-time', label: 'On Time' },
    { n: 'Dr. Lee', s: 'on-time', label: 'On Time' },
    { n: 'Dr. Patel', s: 'late-1', label: '15 mins late' },
    { n: 'Dr. Owens', s: 'on-time', label: 'On Time' },
    { n: 'Dr. Tran', s: 'late-2', label: '30 mins late' },
  ];
  const card3 = (
    <div className="prev-screen">
      <div className="prev-screen-top">
        <span className="mono">APPOINTMENT STATUS</span>
        <span className="mono pulse-dot">● LIVE</span>
      </div>
      <table className="prev-roster">
        <tbody>
          {times.map((d, i) => (
            <tr key={i}>
              <td>{d.n}</td>
              <td><span className={`pill pill--${d.s}`}>{d.label}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const cards = [card1, card2, card3];

  return (
    <div className="prev prev--signage prev--shuffle">
      {cards.map((card, i) => (
        <div
          key={i}
          className="prev-shuffle-card"
          style={{ ...getStyle(i), transition: 'all 0.7s cubic-bezier(0.4, 0, 0.2, 1)' }}
        >
          {card}
        </div>
      ))}
    </div>
  );
}
function PreviewBeds() {
  const beds = Array.from({ length: 24 }).map((_, i) => i % 4 === 0 ? 0 : i % 5 === 0 ? 2 : 1);
  return (
    <div className="prev prev--beds">
      <div className="prev-screen">
        <div className="prev-screen-top">
          <span className="mono">WARD 3 · BED STATUS</span>
          <span className="mono">18 / 24 occ.</span>
        </div>
        <div className="prev-bed-grid">
          {beds.map((s, i) =>
          <div key={i} className={`prev-bed prev-bed--${s}`}>
              <span className="mono">B{String(i + 1).padStart(2, '0')}</span>
            </div>
          )}
        </div>
      </div>
    </div>);

}
function PreviewEPoster() {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const durations = [3200, 1500, 3200, 1500, 2800, 1500];
    const t = setTimeout(() => setPhase(p => (p + 1) % 6), durations[phase]);
    return () => clearTimeout(t);
  }, [phase]);

  const sceneZoomed = phase >= 1 && phase <= 4;
  const posterZoomed = phase === 3 || phase === 4;
  const cssAnim = '@keyframes epRipple{0%{transform:scale(.2);opacity:.85}45%{transform:scale(1.7);opacity:0}50%{transform:scale(.2);opacity:.85}95%{transform:scale(1.7);opacity:0}100%{opacity:0}}@keyframes epDot{0%,100%{transform:scale(1)}12%{transform:scale(.55)}24%{transform:scale(1)}36%{transform:scale(.55)}48%{transform:scale(1)}}.ep-ring{position:absolute;left:50%;top:50%;width:22px;height:22px;margin:-11px 0 0 -11px;border:1.5px solid #0066ff;border-radius:50%;animation:epRipple 1.6s ease-out infinite}.ep-dot{position:absolute;left:50%;top:50%;width:8px;height:8px;margin:-4px 0 0 -4px;background:#0066ff;border-radius:50%;animation:epDot 1.6s ease-in-out infinite;box-shadow:0 0 6px rgba(0,102,255,.7)}';

  const cellArt = [[24,18,3.2],[40,30,2.4],[58,20,3.6],[74,38,2.8],[90,24,2.2],[34,46,2.6],[66,52,3],[88,46,2.4],[18,38,2],[100,36,2.8]];

  return (
    <div style={{width:'100%',borderRadius:6,overflow:'hidden',border:'1px solid #e5e7eb',background:'linear-gradient(180deg, #e8ecf4 0%, #d4dce8 60%, #c0c8d8 100%)',position:'relative',aspectRatio:'16/10'}}>
      <style>{cssAnim}</style>
      <div style={{position:'absolute',inset:0,transition:'transform 1.2s cubic-bezier(0.4,0,0.2,1)',transformOrigin:'50% 38%',transform:sceneZoomed?'scale(2.1)':'scale(1)'}}>
        <svg viewBox="0 0 320 200" width="100%" height="100%" style={{position:'absolute',inset:0,display:'block'}}>
          <defs>
            
          </defs>
          <defs><linearGradient id="ep-bg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#e8ecf4"/><stop offset="60%" stopColor="#d4dce8"/><stop offset="100%" stopColor="#c0c8d8"/></linearGradient></defs><rect width="320" height="200" fill="url(#ep-bg)"/>
          <rect x="0" y="132" width="320" height="68" fill="#bcc5d6" opacity="0.4"/>
          <rect x="0" y="132" width="320" height="0.5" fill="#a0aab8" opacity="0.3"/>
          <rect x="40" y="4" width="240" height="16" rx="8" fill="#fff" opacity="0.7"/><text x="160" y="14.5" textAnchor="middle" fill="#3b5280" fontSize="6.5" fontWeight="800" letterSpacing="0.22em">SYDNEY HEALTH RESEARCH EXPO 2026</text>
          
          <g opacity="0.3"><path d="M42 100a5 5 0 1 0 0-10 5 5 0 0 0 0 10zm0 1c-4 0-7 2-8 4l-2 10c0 1 .5 2 1.5 2h17c1 0 1.5-1 1.5-2l-2-10c-1-2-4-4-8-4zm-3 17l-1 14h3l1.5-10h-0.5l1.5 10h3l-1-14z" fill="#6b7a94"/></g>
          <g opacity="0.22"><path d="M66 105a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9zm0 1c-3.5 0-6.5 2-7.5 4l-1.5 9c0 1 .5 1.5 1.5 1.5h15c1 0 1.5-.5 1.5-1.5l-1.5-9c-1-2-4-4-7.5-4zm-2.5 15l-.5 12h2.5l1-9h-.5l1 9h2.5l-.5-12z" fill="#8494ac"/></g>
          <g opacity="0.35"><path d="M90 96a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11zm0 1c-4.5 0-8 2-9 4.5l-2 11c0 1 .5 2 1.5 2h19c1 0 1.5-1 1.5-2l-2-11c-1-2.5-4.5-4.5-9-4.5zm-3 18l-1 16h3l1.5-12h-.5l1.5 12h3l-1-16z" fill="#5a6b82"/></g>
          <g opacity="0.25"><path d="M240 103a5 5 0 1 0 0-10 5 5 0 0 0 0 10zm0 1c-4 0-7 2-8 4l-2 10c0 1 .5 2 1.5 2h17c1 0 1.5-1 1.5-2l-2-10c-1-2-4-4-8-4zm-3 17l-1 14h3l1.5-10h-.5l1.5 10h3l-1-14z" fill="#708096"/></g>
          <g opacity="0.3"><path d="M266 99a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11zm0 1c-4.5 0-8 2-9 4.5l-2 11c0 1 .5 2 1.5 2h19c1 0 1.5-1 1.5-2l-2-11c-1-2.5-4.5-4.5-9-4.5zm-3 18l-1 15h3l1.5-11h-.5l1.5 11h3l-1-15z" fill="#5e6e86"/></g>
          <g opacity="0.2"><path d="M292 106a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9zm0 1c-3.5 0-6.5 2-7.5 4l-1.5 9c0 1 .5 1.5 1.5 1.5h15c1 0 1.5-.5 1.5-1.5l-1.5-9c-1-2-4-4-7.5-4zm-2.5 15l-.5 11h2.5l1-8h-.5l1 8h2.5l-.5-11z" fill="#8898ae"/></g>
          <rect x="150" y="120" width="20" height="70" rx="1" fill="#4a5568"/>
          <rect x="140" y="189" width="40" height="3" rx="1" fill="#5a6578"/>
          <rect x="126" y="26" width="68" height="100" rx="3" fill="#2d3748" stroke="#4a5568" strokeWidth="0.8"/>
        </svg>
        <div style={{position:'absolute',left:'40.625%',top:'15%',width:'18.75%',height:'46%',overflow:'hidden',background:'#fff'}}>
          <div style={{position:'absolute',inset:0,transition:'transform 1.2s cubic-bezier(0.4,0,0.2,1)',transformOrigin:'33% 82%',transform:posterZoomed?'scale(3)':'scale(1)'}}>
            <svg viewBox="0 0 260 400" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" style={{display:'block'}}>
              <rect width="260" height="400" fill="#fff"/>
              <rect x="0" y="0" width="260" height="42" fill="#0a2a5c"/>
              <text x="10" y="17" fill="#fff" fontSize="9" fontWeight="800" letterSpacing="-0.02em">EP-142</text>
              <text x="10" y="27" fill="rgba(255,255,255,0.65)" fontSize="5" fontWeight="500">Sydney Health Research Expo 2026</text>
              <text x="10" y="35" fill="rgba(255,255,255,0.45)" fontSize="4.5">Oncology Track · Pediatric Cancer</text>
              <rect x="212" y="11" width="38" height="14" rx="3" fill="#0066ff"/>
              <circle cx="221" cy="18" r="1.6" fill="#fff"/><text x="226" y="20" fill="#fff" fontSize="6" fontWeight="700">LIVE</text>
              <text x="12" y="61" fill="#111" fontSize="13" fontWeight="800" letterSpacing="-0.02em">Novel CAR-T Cell Therapy</text>
              <text x="12" y="77" fill="#0066ff" fontSize="13" fontWeight="800" letterSpacing="-0.02em">in Pediatric Leukaemia</text>
              <line x1="12" y1="82" x2="120" y2="82" stroke="#0066ff" strokeWidth="1"/>
              <text x="12" y="91" fill="#475569" fontSize="6" fontWeight="600">L. Chen, R. Patel, M. Rodriguez, S. Wu</text>
              <text x="12" y="98" fill="#64748b" fontSize="5.2">Children's Cancer Institute · UNSW Sydney</text>

              <rect x="12" y="104" width="114" height="82" rx="2" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="0.6"/>
              <text x="18" y="116" fill="#0a2a5c" fontSize="6.5" fontWeight="800">BACKGROUND</text>
              <line x1="18" y1="119" x2="56" y2="119" stroke="#0066ff" strokeWidth="0.6"/>
              <text x="18" y="128" fill="#475569" fontSize="5">CAR-T therapy reprograms a</text>
              <text x="18" y="135" fill="#475569" fontSize="5">patient's T-cells to attack</text>
              <text x="18" y="142" fill="#475569" fontSize="5">cancer. In B-cell ALL, single</text>
              <text x="18" y="149" fill="#475569" fontSize="5">CD19 targeting risks antigen</text>
              <text x="18" y="156" fill="#475569" fontSize="5">escape, with relapse in up to</text>
              <text x="18" y="163" fill="#475569" fontSize="5">30% of pediatric cases within</text>
              <text x="18" y="170" fill="#475569" fontSize="5">12 months of remission. A</text>
              <text x="18" y="177" fill="#475569" fontSize="5">dual-target approach may</text>
              <text x="18" y="184" fill="#475569" fontSize="5">reduce this escape risk.</text>

              <rect x="134" y="104" width="114" height="82" rx="2" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="0.6"/>
              <text x="140" y="116" fill="#0a2a5c" fontSize="6.5" fontWeight="800">METHODOLOGY</text>
              <line x1="140" y1="119" x2="186" y2="119" stroke="#0066ff" strokeWidth="0.6"/>
              <text x="140" y="128" fill="#475569" fontSize="5">Phase II randomised trial</text>
              <text x="140" y="135" fill="#475569" fontSize="5">(n=240) across 6 Australian</text>
              <text x="140" y="142" fill="#475569" fontSize="5">centres, ages 3-17. Dual</text>
              <text x="140" y="149" fill="#475569" fontSize="5">CD19/CD22 CAR-T compared</text>
              <text x="140" y="156" fill="#475569" fontSize="5">against standard CD19</text>
              <text x="140" y="163" fill="#475569" fontSize="5">mono-CAR-T. Primary</text>
              <text x="140" y="170" fill="#475569" fontSize="5">endpoint: 12-month event-</text>
              <text x="140" y="177" fill="#475569" fontSize="5">free survival (EFS). Secondary:</text>
              <text x="140" y="184" fill="#475569" fontSize="5">MRD negativity at 6 months.</text>

              <rect x="12" y="192" width="114" height="88" rx="2" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="0.6"/>
              <text x="18" y="203" fill="#0a2a5c" fontSize="6" fontWeight="800">FIGURE 1</text>
              <rect x="18" y="207" width="102" height="56" rx="1.5" fill="#1a0a2e"/>
              <rect x="18" y="207" width="102" height="56" rx="1.5" fill="url(#cellgrad)"/>
              <defs><radialGradient id="cellgrad" cx="50%" cy="45%"><stop offset="0%" stopColor="#3a1a5c"/><stop offset="100%" stopColor="#1a0a2e"/></radialGradient></defs>
              {cellArt.map((p,i)=>(<g key={i}><circle cx={18+p[0]} cy={207+p[1]} r={p[2]} fill="#d946ef" opacity="0.45"/><circle cx={18+p[0]} cy={207+p[1]} r={p[2]*0.5} fill="#f0abfc" opacity="0.6"/></g>))}
              <circle cx="60" cy="232" r="7" fill="none" stroke="#22d3ee" strokeWidth="1" opacity="0.8"/>
              <circle cx="84" cy="240" r="6" fill="none" stroke="#22d3ee" strokeWidth="1" opacity="0.8"/>
              <text x="18" y="271" fill="#475569" fontSize="4.4">Fig 1. CAR-T cells (cyan) binding</text>
              <text x="18" y="277" fill="#475569" fontSize="4.4">CD19/CD22 leukaemic cells (magenta)</text>

              <rect x="134" y="192" width="114" height="88" rx="2" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="0.6"/>
              <text x="140" y="203" fill="#0a2a5c" fontSize="6" fontWeight="800">FIGURE 2</text>
              <rect x="140" y="207" width="102" height="56" rx="1.5" fill="#fff" stroke="#e2e8f0" strokeWidth="0.4"/>
              <line x1="148" y1="212" x2="148" y2="256" stroke="#cbd5e1" strokeWidth="0.5"/>
              <line x1="148" y1="256" x2="236" y2="256" stroke="#cbd5e1" strokeWidth="0.5"/>
              <polyline points="148,216 168,217 188,219 208,222 236,225" fill="none" stroke="#22c55e" strokeWidth="1.2"/>
              <polyline points="148,216 168,224 188,234 208,244 236,250" fill="none" stroke="#ef4444" strokeWidth="1.2" strokeDasharray="2 1.5"/>
              <text x="192" y="220" fill="#22c55e" fontSize="4.2" fontWeight="700">Dual CAR-T</text>
              <text x="192" y="248" fill="#ef4444" fontSize="4.2" fontWeight="700">Standard</text>
              <text x="140" y="271" fill="#475569" fontSize="4.4">Fig 2. Kaplan-Meier survival over</text>
              <text x="140" y="277" fill="#475569" fontSize="4.4">24 months by treatment arm</text>

              <rect x="12" y="286" width="138" height="74" rx="2" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="0.6"/>
              <text x="18" y="297" fill="#0a2a5c" fontSize="5.8" fontWeight="800">RESULTS — 12-MONTH EFS (%)</text>
              <line x1="26" y1="303" x2="26" y2="350" stroke="#cbd5e1" strokeWidth="0.4"/>
              <line x1="26" y1="350" x2="144" y2="350" stroke="#cbd5e1" strokeWidth="0.4"/>
              <line x1="26" y1="303" x2="144" y2="303" stroke="#eef2f6" strokeWidth="0.3"/>
              <line x1="26" y1="326" x2="144" y2="326" stroke="#eef2f6" strokeWidth="0.3"/>
              {[[34,32,'#94a3b8','Ctrl'],[52,48,'#0066ff','10mg'],[70,71,'#0066ff','25mg'],[88,89,'#0066ff','50mg'],[106,78,'#0066ff','75mg'],[124,95,'#22c55e','Cmb']].map((b,i)=>{const h=b[1]*0.47;return(<g key={i}><rect x={b[0]-5} y={350-h} width="10" height={h} rx="0.6" fill={b[2]}/><text x={b[0]} y={348-h} textAnchor="middle" fill={b[2]} fontSize="3.8" fontWeight="700">{b[1]}%</text><text x={b[0]} y={356} textAnchor="middle" fill="#64748b" fontSize="3.6">{b[3]}</text></g>);})}

              <rect x="156" y="286" width="92" height="74" rx="2" fill="#0a2a5c"/>
              <text x="162" y="297" fill="rgba(255,255,255,0.7)" fontSize="5.5" fontWeight="700">KEY FINDINGS</text>
              <text x="162" y="312" fill="#4ade80" fontSize="11" fontWeight="800">95%</text><text x="186" y="312" fill="rgba(255,255,255,0.8)" fontSize="4.6">12-month EFS</text>
              <text x="162" y="328" fill="#fff" fontSize="9" fontWeight="800">n=240</text><text x="194" y="328" fill="rgba(255,255,255,0.8)" fontSize="4.6">enrolled</text>
              <text x="162" y="343" fill="#fff" fontSize="9" fontWeight="800">p&lt;0.001</text><text x="200" y="343" fill="rgba(255,255,255,0.8)" fontSize="4.6">sig.</text>
              <text x="162" y="356" fill="#fff" fontSize="7" fontWeight="700">0 antigen-escape relapses</text>

              <rect x="12" y="366" width="236" height="24" rx="2" fill="#0066ff"/>
              <text x="130" y="377" textAnchor="middle" fill="#fff" fontSize="5.5" fontWeight="700">Dual-targeting CAR-T achieved 95% EFS</text>
              <text x="130" y="385" textAnchor="middle" fill="rgba(255,255,255,0.85)" fontSize="5">a 63% improvement over standard therapy</text>
            </svg>
          </div>
          {phase === 2 && (
            <div style={{position:'absolute',left:'33%',top:'82%',width:0,height:0,zIndex:5}}>
              <div className="ep-ring" />
              <div className="ep-dot" />
            </div>
          )}
        </div>
      </div>
      {phase === 2 && (
        <div style={{position:'absolute',bottom:8,left:'50%',transform:'translateX(-50%)',background:'#2d3748',color:'#fff',fontSize:8,fontWeight:600,padding:'3px 10px',borderRadius:10,letterSpacing:'0.03em',whiteSpace:'nowrap'}}>Double-tap to zoom into any detail</div>
      )}
    </div>
  );
}



// ---------------- Stat counter -----------------
function Stat({ v, l }) {
  return (
    <div className="stat">
      <div className="stat-v">{v}</div>
      <div className="stat-l">{l}</div>
    </div>);

}

// ---------------- Page transitions wrapper -----------------
function Page({ children, name }) {
  return <main className="page" data-page={name}>{children}</main>;
}

export { ScrollReveal, CursorParallax, BrandMark, KineticHero, SignageWall, ProductPreview, Stat, Page, ScrollScene, ScrollHUD };
